// src/components/ReservationDrawer.jsx
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { createAuditedClient } from '../utils/auditClient';
import { Button, DangerButton } from '../ui';
import { ProposeChange } from '../graphqlOps';
import { useNavigate } from 'react-router-dom';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import { format } from 'date-fns';
import {
  getReservationCarSegments,
  reservationUsesCarInWindow,
} from '../utils/reservationCarSegments';

/* ================= GraphQL ================= */
const client = createAuditedClient('ReservationDrawer');

const BaseReservationFields = `
  id
  CompanyId
  carId
  originalCarId
  originalCarName
  originalCarPlate
  carSubstitutions
  status
  title
  start
  end
  pickupAt
  pickupLocation
  dropoffAt
  dropoffLocation
  reservationDate
  reservationRef
  customerName
  customerEmail
  customerPhone
  flightNumber
  phoneNumbers
  babyCarSeat
  extras          
  depositAmount
  rentalAmount
  depositCurrency
  rentalCurrency
  platform
  senderEmail
  acceptUrl
  declineUrl
  notes
  approvalStatus
  pickupChecklistStatus
  dropoffChecklistStatus
  paymentStatus    
  amountPaid
  createdAt
  updatedAt
`;

const BaseMaintenanceFields = `
  id
  CompanyId
  carId
  title
  start
  end
  notes
  approvalStatus
  createdAt
  updatedAt
`;

const ByCompanyByStart_DT = /* GraphQL */ `
  query ByCompanyByStart($CompanyId: String!, $start: ModelStringKeyConditionInput) {
    reservationsByCompanyByStart(CompanyId: $CompanyId, start: $start, limit: 1000) {
      items { ${BaseReservationFields} }
      nextToken
    }
  }
`;
const ByCompanyByStart_STR = /* GraphQL */ `
  query ByCompanyByStart($CompanyId: String!, $start: ModelStringKeyConditionInput) {
    reservationsByCompanyByStart(CompanyId: $CompanyId, start: $start, limit: 1000) {
      items { ${BaseReservationFields} }
      nextToken
    }
  }
`;

const MaintByCompanyByStart_DT = /* GraphQL */ `
  query MaintByCompanyByStart($CompanyId: String!, $start: ModelStringKeyConditionInput) {
    maintenanceByCompanyByStart(CompanyId: $CompanyId, start: $start, limit: 1000) {
      items { ${BaseMaintenanceFields} }
      nextToken
    }
  }
`;
const MaintByCompanyByStart_STR = /* GraphQL */ `
  query MaintByCompanyByStart($CompanyId: String!, $start: ModelStringKeyConditionInput) {
    maintenanceByCompanyByStart(CompanyId: $CompanyId, start: $start, limit: 1000) {
      items { ${BaseMaintenanceFields} }
      nextToken
    }
  }
`;

// Mutations
const CreateReservation = /* GraphQL */ `
  mutation CreateReservation($input: CreateReservationInput!) {
    createReservation(input: $input) { ${BaseReservationFields} }
  }
`;
const UpdateReservation = /* GraphQL */ `
  mutation UpdateReservation($input: UpdateReservationInput!) {
    updateReservation(input: $input) { ${BaseReservationFields} }
  }
`;
const DeleteReservation = /* GraphQL */ `
  mutation DeleteReservation($input: DeleteReservationInput!) {
    deleteReservation(input: $input) { id }
  }
`;

const CreateMaintenance = /* GraphQL */ `
  mutation CreateMaintenance($input: CreateMaintenanceInput!) {
    createMaintenance(input: $input) { ${BaseMaintenanceFields} }
  }
`;
const UpdateMaintenance = /* GraphQL */ `
  mutation UpdateMaintenance($input: UpdateMaintenanceInput!) {
    updateMaintenance(input: $input) { ${BaseMaintenanceFields} }
  }
`;
const DeleteMaintenance = /* GraphQL */ `
  mutation DeleteMaintenance($input: DeleteMaintenanceInput!) {
    deleteMaintenance(input: $input) { id }
  }
`;

/* =============== Helpers =============== */
const LOOKBACK_DAYS = 31;
const RESERVATION_ID_PREFIX = 'RES';
const RESERVATION_ID_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

function randomReservationIdChunk(size = 6) {
  const alphabet = RESERVATION_ID_ALPHABET;
  const alphabetSize = alphabet.length;

  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = new Uint8Array(size);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => alphabet[b % alphabetSize]).join('');
  }

  let out = '';
  for (let i = 0; i < size; i += 1) {
    out += alphabet[Math.floor(Math.random() * alphabetSize)];
  }
  return out;
}

function createReadableReservationId(now = new Date()) {
  const d = now instanceof Date && !Number.isNaN(now.getTime()) ? now : new Date();
  const yyyy = String(d.getUTCFullYear());
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${RESERVATION_ID_PREFIX}-${yyyy}${mm}${dd}-${randomReservationIdChunk(6)}`;
}

// Always store + compare as true UTC ISO strings.
// Convert to local only for the form fields.
function tzForCompanyCountry(country) {
  const c = String(country || '').trim().toUpperCase();
  if (c === 'GE' || c === 'GEORGIA') return 'Asia/Tbilisi';
  if (c === 'AL' || c === 'ALBANIA') return 'Europe/Tirane';
  if (c === 'LB' || c === 'LEBANON') return 'Asia/Beirut';
  return 'UTC';
}

function toUtcIsoFromCompanyParts(dateYmd, timeHm, tz) {
  if (!dateYmd || !timeHm) return null;
  const s = `${dateYmd}T${timeHm}:00`;
  const utcDate = fromZonedTime(s, tz); // interpret as wall-clock in tz
  return Number.isNaN(utcDate.getTime()) ? null : utcDate.toISOString();
}

function toIsoStringSafe(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.toISOString();
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function normalizePaymentStatusValue(value) {
  const v = String(value || '').trim().toUpperCase();
  if (!v) return 'NOT_PAID';
  if (v === 'PREPAID' || v === 'PAID' || v === 'FULLY_PAID') return 'PREPAID';
  if (v === 'PARTIALLY_PAID' || v === 'PARTIAL') return 'PARTIALLY_PAID';
  return 'NOT_PAID';
}

function normalizeIncomingPaymentState(paymentStatus, amountPaid, rentalAmount) {
  const normalizedStatus = normalizePaymentStatusValue(paymentStatus);
  const paidNum = amountPaid === '' || amountPaid == null ? NaN : parseFloat(amountPaid);
  const rentalNum = rentalAmount === '' || rentalAmount == null ? NaN : parseFloat(rentalAmount);
  const hasPaid = Number.isFinite(paidNum) && paidNum > 0;
  const isFullyPaid = hasPaid && Number.isFinite(rentalNum) && paidNum >= rentalNum - 0.01;

  if (normalizedStatus === 'PREPAID') {
    return {
      paymentStatus: isFullyPaid ? 'PREPAID' : 'NOT_PAID',
      amountPaid: isFullyPaid ? String(amountPaid ?? '') : '',
    };
  }

  if (normalizedStatus === 'PARTIALLY_PAID') {
    return {
      paymentStatus: hasPaid ? 'PARTIALLY_PAID' : 'NOT_PAID',
      amountPaid: hasPaid ? String(amountPaid ?? '') : '',
    };
  }

  return {
    paymentStatus: 'NOT_PAID',
    amountPaid: '',
  };
}


function isoNDaysBeforeUtc(utcIso, n) {
  if (!utcIso) return '';
  const d = new Date(utcIso);
  if (Number.isNaN(d.getTime())) return '';
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString();
}

// Key condition for overlap queries (UTC ISO)
function overlapKey(endUtcIso) {
  if (!endUtcIso) return undefined;
  const hi = endUtcIso;
  const lo = isoNDaysBeforeUtc(hi, LOOKBACK_DAYS);
  if (!lo) return { ge: hi };
  return { between: [lo, hi] };
}

// ISO UTC strings sort lexicographically, so this is safe if EVERYTHING is UTC ISO.
function overlapsString(startA, endA, startB, endB) {
  if (!startA || !endA || !startB || !endB) return false;
  return startA < endB && startB < endA;
}

// For inputs: show LOCAL date+time derived from stored UTC ISO
function splitCompanyDateTime(utcIso, tz) {
  if (!utcIso) return { date: '', time: '' };
  const d = new Date(utcIso);
  if (Number.isNaN(d.getTime())) return { date: '', time: '' };

  const z = toZonedTime(d, tz); // shift for display in tz
  return {
    date: format(z, 'yyyy-MM-dd'),
    time: format(z, 'HH:mm'),
  };
}

function getApprovalStatus(row) {
  return (row?.approvalStatus || 'APPROVED').toUpperCase();
}
function isBlocking(row) {
  const st = getApprovalStatus(row);
  return st === 'APPROVED' || st === 'PENDING';
}

function sanitizeJson(value) {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (Number.isNaN(value)) return null;
  if (value instanceof Date) return value.toISOString();

  if (Array.isArray(value)) return value.map(sanitizeJson);

  if (value && typeof value === 'object') {
    const out = {};
    for (const k of Object.keys(value)) {
      const v = sanitizeJson(value[k]);
      if (v !== undefined) out[k] = v;
    }
    return out;
  }
  return value;
}

function parseJsonLayers(value, maxDepth = 8) {
  let current = value;

  for (let i = 0; i < maxDepth; i++) {
    if (typeof current !== 'string') break;
    const trimmed = current.trim();
    if (!trimmed) return '';
    try {
      current = JSON.parse(trimmed);
    } catch {
      break;
    }
  }

  return current;
}

function collectPhoneTokens(value, out, depth = 0) {
  if (value == null || depth > 12) return;

  const decoded = parseJsonLayers(value);

  if (Array.isArray(decoded)) {
    decoded.forEach((item) => collectPhoneTokens(item, out, depth + 1));
    return;
  }

  if (decoded && typeof decoded === 'object') return;

  const text = String(decoded).trim();
  if (!text) return;

  if (text.includes(',')) {
    text
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((part) => out.push(part));
    return;
  }

  out.push(text);
}

function normalizePhoneNumbersArray(value) {
  const raw = [];
  collectPhoneTokens(value, raw);

  const seen = new Set();
  const normalized = [];
  for (const phone of raw) {
    if (seen.has(phone)) continue;
    seen.add(phone);
    normalized.push(phone);
  }
  return normalized;
}

function phoneNumbersToInputString(value) {
  return normalizePhoneNumbersArray(value).join(', ');
}

function phoneNumbersToAwsJson(value) {
  const phones = normalizePhoneNumbersArray(value);
  return phones.length ? JSON.stringify(phones) : null;
}

const EXTRA_DRIVERS_PREFIX = 'extra_drivers:';
const EXTRA_DRIVER_PREFIX = 'extra_driver:';
const MAX_EXTRA_DRIVERS = 10;
const MAX_SEAT_EXTRA_COUNT = 3;
const SEAT_EXTRA_OPTIONS = [
  { value: 'booster_seat', label: 'Booster seat (4+ years)' },
  { value: 'infant_seat', label: 'Infant seat (9 months - 4 years)' },
  { value: 'baby_seat', label: 'Baby seat (0 - 9 months)' },
];

function normalizeExtraDriversCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(MAX_EXTRA_DRIVERS, Math.floor(n)));
}

function normalizeSeatExtraCount(value, selectedFallback = false) {
  const n = Number(value);
  if (Number.isFinite(n)) return Math.max(0, Math.min(MAX_SEAT_EXTRA_COUNT, Math.floor(n)));
  return selectedFallback ? 1 : 0;
}

function normalizeSeatExtraMatchKey(value) {
  return String(value || '').trim().toLowerCase().replace(/[\s_-]+/g, '');
}

function seatExtraOptionForKey(key) {
  const normalized = normalizeSeatExtraMatchKey(key);
  const aliasesByValue = {
    booster_seat: ['boosterSeat', 'booster_seat', 'boosterSeats'],
    infant_seat: ['infantSeat', 'infant_seat', 'infantSeats'],
    baby_seat: ['babySeat', 'baby_seat', 'babyCarSeat', 'baby_car_seat', 'babySeats'],
  };

  return SEAT_EXTRA_OPTIONS.find((opt) => {
    const aliases = [
      opt.value,
      ...(aliasesByValue[opt.value] || []),
      ...(aliasesByValue[opt.value] || []).flatMap((baseKey) => [
        `${baseKey}Count`,
        `${baseKey}Quantity`,
        `${baseKey}Qty`,
      ]),
    ];
    return aliases.some((alias) => normalizeSeatExtraMatchKey(alias) === normalized);
  }) || null;
}

function parseSeatExtraCountToken(rawToken) {
  const token = String(rawToken || '').trim().toLowerCase();
  if (!token) return null;
  const match = token.match(/^([a-zA-Z][a-zA-Z0-9_\s-]*?)\s*[:=]\s*(\d+)$/);
  if (!match) return null;
  const opt = seatExtraOptionForKey(match[1]);
  if (!opt) return null;
  return { value: opt.value, count: normalizeSeatExtraCount(match[2]) };
}

function seatExtraCountToken(value, count) {
  const normalizedCount = normalizeSeatExtraCount(count);
  if (normalizedCount <= 0) return null;
  return normalizedCount === 1 ? value : `${value}:${normalizedCount}`;
}

function isSeatExtraTokenForValue(tokenValue, value) {
  const token = String(tokenValue || '').trim().toLowerCase();
  if (!token) return false;
  if (token === value) return true;
  const parsed = parseSeatExtraCountToken(token);
  return parsed?.value === value;
}

function seatExtraCountFromExtras(extrasArr, value) {
  let count = 0;
  for (const raw of Array.isArray(extrasArr) ? extrasArr : []) {
    const token = String(raw || '').trim().toLowerCase();
    if (!token) continue;
    const parsed = parseSeatExtraCountToken(token);
    if (parsed?.value === value) {
      count = Math.max(count, parsed.count);
      continue;
    }
    if (token === value) count = Math.max(count, 1);
  }
  return normalizeSeatExtraCount(count);
}

function withSeatExtraCount(extrasArr, value, count) {
  const current = Array.isArray(extrasArr) ? extrasArr : [];
  const next = current.filter((token) => !isSeatExtraTokenForValue(token, value));
  const countToken = seatExtraCountToken(value, count);
  if (countToken) next.push(countToken);
  return Array.from(new Set(next));
}

function parseExtraDriversCountToken(rawToken) {
  const token = String(rawToken || '').trim().toLowerCase();
  if (!token) return null;

  if (token === 'extra_driver' || token === 'extra_drivers' || token === 'extradriver' || token === 'extradrivers') {
    return 1;
  }

  if (token.startsWith(EXTRA_DRIVERS_PREFIX)) {
    return normalizeExtraDriversCount(token.slice(EXTRA_DRIVERS_PREFIX.length));
  }
  if (token.startsWith(EXTRA_DRIVER_PREFIX)) {
    return normalizeExtraDriversCount(token.slice(EXTRA_DRIVER_PREFIX.length));
  }

  const match = token.match(/^extra[\s_-]*drivers?\s*[:=]\s*(\d+)$/i);
  if (match) return normalizeExtraDriversCount(match[1]);

  return null;
}

function extraDriversCountToToken(value) {
  const n = normalizeExtraDriversCount(value);
  return n > 0 ? `${EXTRA_DRIVERS_PREFIX}${n}` : null;
}

const CROSS_BORDER_COUNTRY_OPTIONS = ['Kosovo', 'Greece', 'North Macedonia', 'Montenegro'];
const CROSS_BORDER_COUNTRY_TOKEN_PREFIX = 'cross_border_country:';

function normalizeCrossBorderCountryKey(value) {
  return String(value || '').trim().toLowerCase().replace(/[\s_-]+/g, '');
}

function normalizeCrossBorderCountries(value) {
  const byKey = CROSS_BORDER_COUNTRY_OPTIONS.reduce((acc, country) => {
    acc[normalizeCrossBorderCountryKey(country)] = country;
    return acc;
  }, {});
  const raw = [];
  const add = (item) => {
    if (Array.isArray(item)) {
      item.forEach(add);
      return;
    }
    const s = String(item || '').trim();
    if (!s) return;
    if (s.includes(',')) {
      s.split(',').forEach(add);
      return;
    }
    raw.push(s);
  };

  add(value);

  const out = [];
  for (const item of raw) {
    const clean = item.replace(/^cross[\s_-]*border[\s_-]*country\s*[:=]\s*/i, '');
    const country = byKey[normalizeCrossBorderCountryKey(clean)];
    if (country && !out.includes(country)) out.push(country);
  }
  return out;
}

function crossBorderCountryToken(country) {
  const normalized = normalizeCrossBorderCountries(country)[0];
  return normalized ? `${CROSS_BORDER_COUNTRY_TOKEN_PREFIX}${normalized.toLowerCase().replace(/\s+/g, '_')}` : '';
}

function crossBorderCountriesFromExtras(extrasArr) {
  const countries = [];
  for (const raw of Array.isArray(extrasArr) ? extrasArr : []) {
    const token = String(raw || '').trim();
    if (!token.toLowerCase().startsWith(CROSS_BORDER_COUNTRY_TOKEN_PREFIX)) continue;
    for (const country of normalizeCrossBorderCountries(token)) {
      if (!countries.includes(country)) countries.push(country);
    }
  }
  return countries;
}

function normalizeExtrasArray(v, fallbackBabyCarSeat) {
  const normalizeExtraKey = (rawKey) => {
    const key = String(rawKey || '').trim();
    if (!key) return null;

    const map = {
      boosterSeat: 'booster_seat',
      booster_seat: 'booster_seat',
      infantSeat: 'infant_seat',
      infant_seat: 'infant_seat',
      babySeat: 'baby_seat',
      baby_seat: 'baby_seat',
      babyCarSeat: 'baby_seat',
      baby_car_seat: 'baby_seat',
      snowChains: 'snow_chains',
      snow_chains: 'snow_chains',
      gps: 'gps',
      GPS: 'gps',
      bluetoothReceiver: 'bluetooth_receiver',
      bluetooth_receiver: 'bluetooth_receiver',
      crossBorder: 'cross_border',
      cross_border: 'cross_border',
      crossborder: 'cross_border',
      extraDriver: 'extra_driver',
      extra_driver: 'extra_driver',
      extraDrivers: 'extra_drivers',
      extra_drivers: 'extra_drivers',
    };

    return map[key] || map[key.toLowerCase()] || key.toLowerCase();
  };

  const normalizeArray = (arr) => {
    const out = [];
    let extraDriversCount = 0;
    const seatCounts = {};

    for (const item of arr || []) {
      const seatCount = parseSeatExtraCountToken(item);
      if (seatCount) {
        seatCounts[seatCount.value] = Math.max(seatCounts[seatCount.value] || 0, seatCount.count);
        continue;
      }

      const tokenCount = parseExtraDriversCountToken(item);
      if (tokenCount != null) {
        extraDriversCount = Math.max(extraDriversCount, tokenCount);
        continue;
      }

      const mapped = normalizeExtraKey(item);
      const seatOpt = seatExtraOptionForKey(mapped);
      if (seatOpt) {
        seatCounts[seatOpt.value] = Math.max(seatCounts[seatOpt.value] || 0, 1);
      } else if (mapped) {
        out.push(mapped);
      }
    }

    const countToken = extraDriversCountToToken(extraDriversCount);
    if (countToken) out.push(countToken);
    for (const opt of SEAT_EXTRA_OPTIONS) {
      const seatToken = seatExtraCountToken(opt.value, seatCounts[opt.value] || 0);
      if (seatToken) out.push(seatToken);
    }

    return Array.from(new Set(out.filter(Boolean)));
  };

  const normalizeObject = (obj) => {
    const out = [];
    let extraDriversCount = 0;
    const seatCounts = {};

    for (const [k, val] of Object.entries(obj || {})) {
      const key = String(k || '').trim().toLowerCase();
      const seatOpt = seatExtraOptionForKey(k);
      if (seatOpt) {
        const isCountField = /count|quantity|qty|seats?$/i.test(String(k || ''));
        const count = isCountField
          ? normalizeSeatExtraCount(val)
          : normalizeSeatExtraCount(null, !!val);
        seatCounts[seatOpt.value] = Math.max(seatCounts[seatOpt.value] || 0, count);
        continue;
      }

      if (
        key === 'extradriverscount' ||
        key === 'extra_drivers_count' ||
        key === 'extra_driver_count'
      ) {
        extraDriversCount = Math.max(extraDriversCount, normalizeExtraDriversCount(val));
        continue;
      }

      if (key === 'extradriver' || key === 'extra_driver' || key === 'extra_drivers') {
        if (val) extraDriversCount = Math.max(extraDriversCount, 1);
        continue;
      }

      if (
        key === 'crossbordercountries' ||
        key === 'cross_border_countries' ||
        key === 'crossbordercountry' ||
        key === 'cross_border_country' ||
        key === 'crossborderdestinationcountries'
      ) {
        const countries = normalizeCrossBorderCountries(val);
        if (countries.length) {
          out.push('cross_border');
          countries.forEach((country) => out.push(crossBorderCountryToken(country)));
        }
        continue;
      }

      if (!val) continue;
      const mapped = normalizeExtraKey(k);
      const mappedSeatOpt = seatExtraOptionForKey(mapped);
      if (mappedSeatOpt) {
        seatCounts[mappedSeatOpt.value] = Math.max(seatCounts[mappedSeatOpt.value] || 0, 1);
      } else if (mapped) {
        out.push(mapped);
      }
    }

    const countToken = extraDriversCountToToken(extraDriversCount);
    if (countToken) out.push(countToken);
    for (const opt of SEAT_EXTRA_OPTIONS) {
      const seatToken = seatExtraCountToken(opt.value, seatCounts[opt.value] || 0);
      if (seatToken) out.push(seatToken);
    }

    return Array.from(new Set(out.filter(Boolean)));
  };

  // already array
  if (Array.isArray(v)) return normalizeArray(v);

  // AWSJSON string
  if (typeof v === 'string' && v.trim()) {
    try {
      const parsed = JSON.parse(v);
      if (Array.isArray(parsed)) return normalizeArray(parsed);
      if (parsed && typeof parsed === 'object') return normalizeObject(parsed);
    } catch {
      // ignore
    }
  }

  // object AWSJSON
  if (v && typeof v === 'object') return normalizeObject(v);

  // legacy babyCarSeat -> extras
  if (fallbackBabyCarSeat) return [seatExtraCountToken('baby_seat', 1)];

  return [];
}

function extrasToAwsJson(extrasArr) {
  const arr = Array.isArray(extrasArr) ? extrasArr.filter(Boolean) : [];
  return arr.length ? JSON.stringify(arr) : null;
}

const INSURANCE_INCLUDED_EXTRA = 'insurance_included';
const INSURANCE_NOT_INCLUDED_EXTRA = 'insurance_not_included';
const INSURANCE_AMOUNT_PREFIX = 'insurance_amount:';
const INSURANCE_CURRENCY_PREFIX = 'insurance_currency:';

function normalizeInsuranceIncludedValue(value) {
  const v = String(value || '').trim().toUpperCase();
  if (!v) return '';
  if (v === 'INCLUDED' || v === 'YES' || v === 'TRUE' || v === '1') return 'INCLUDED';
  if (
    v === 'NOT_INCLUDED' ||
    v === 'NOT INCLUDED' ||
    v === 'NO' ||
    v === 'FALSE' ||
    v === '0'
  ) {
    return 'NOT_INCLUDED';
  }
  return '';
}

function splitInsuranceFromExtras(extrasArr) {
  const baseExtras = [];
  let insuranceIncluded = '';
  let insuranceAmount = '';
  let insuranceCurrency = '';
  let extraDriversCount = 0;

  for (const raw of Array.isArray(extrasArr) ? extrasArr : []) {
    const rawToken = String(raw || '').trim();
    if (!rawToken) continue;

    const parsedExtraDrivers = parseExtraDriversCountToken(rawToken);
    if (parsedExtraDrivers != null) {
      extraDriversCount = Math.max(extraDriversCount, parsedExtraDrivers);
      continue;
    }

    const token = rawToken.toLowerCase();

    if (token === INSURANCE_INCLUDED_EXTRA) {
      insuranceIncluded = 'INCLUDED';
      continue;
    }
    if (token === INSURANCE_NOT_INCLUDED_EXTRA) {
      insuranceIncluded = 'NOT_INCLUDED';
      continue;
    }
    if (token.startsWith(INSURANCE_AMOUNT_PREFIX)) {
      insuranceAmount = rawToken.slice(INSURANCE_AMOUNT_PREFIX.length).trim();
      continue;
    }
    if (token.startsWith(INSURANCE_CURRENCY_PREFIX)) {
      insuranceCurrency = rawToken
        .slice(INSURANCE_CURRENCY_PREFIX.length)
        .trim()
        .toUpperCase();
      continue;
    }

    baseExtras.push(token);
  }

  return {
    extras: Array.from(new Set(baseExtras)),
    insuranceIncluded,
    insuranceAmount,
    insuranceCurrency,
    extraDriversCount,
  };
}

function withInsuranceExtras(
  extrasArr,
  insuranceIncluded,
  insuranceAmount,
  insuranceCurrency,
  extraDriversCount
) {
  const cleanBaseExtras = [];

  for (const raw of Array.isArray(extrasArr) ? extrasArr : []) {
    const token = String(raw || '').trim().toLowerCase();
    if (!token) continue;
    if (token === INSURANCE_INCLUDED_EXTRA || token === INSURANCE_NOT_INCLUDED_EXTRA) continue;
    if (token.startsWith(INSURANCE_AMOUNT_PREFIX)) continue;
    if (token.startsWith(INSURANCE_CURRENCY_PREFIX)) continue;
    if (parseExtraDriversCountToken(token) != null) continue;
    cleanBaseExtras.push(token);
  }

  const out = Array.from(new Set(cleanBaseExtras));
  const normalizedIncluded = normalizeInsuranceIncludedValue(insuranceIncluded);
  if (normalizedIncluded === 'INCLUDED') out.push(INSURANCE_INCLUDED_EXTRA);
  if (normalizedIncluded === 'NOT_INCLUDED') out.push(INSURANCE_NOT_INCLUDED_EXTRA);

  const amount = String(insuranceAmount || '').trim();
  if (amount) out.push(`${INSURANCE_AMOUNT_PREFIX}${amount}`);

  const currency = String(insuranceCurrency || '').trim().toUpperCase();
  if (currency) out.push(`${INSURANCE_CURRENCY_PREFIX}${currency}`);

  const countToken = extraDriversCountToToken(extraDriversCount);
  if (countToken) out.push(countToken);

  return out;
}

function sortCarsByName(list) {
  return [...list].sort((a, b) => {
    const an = (a.name || '').toLowerCase();
    const bn = (b.name || '').toLowerCase();
    if (an < bn) return -1;
    if (an > bn) return 1;
    return 0;
  });
}

// Deposit suggestion by country + category
function getSuggestedDeposit(location, category) {
  if (!location) return null;

  const loc = String(location).toUpperCase();
  const cat = category ? String(category).toUpperCase() : null;

  // GEORGIA
  if (loc === 'GE' || loc === 'GEORGIA') {
    // Georgia: flat 400 USD
    return 400;
  }

  // ALBANIA
  if (loc === 'AL' || loc === 'ALBANIA') {
    if (!cat) return null;

    switch (cat) {
      case 'ECONOMY':
        return 600;
      case 'COMPACT':
      case 'COMPACT_ELITE':
        return 800;
      case 'INTERMEDIATE':
      case 'INTERMEDIATE_ELITE':
      case 'INTERMEDIATE_SUV':
      case 'MINIVAN':
        return 1000;
      case 'COMPACT_SUV_CFAR':
        return 1000; // "COMPACT SUV"
      case 'SUV_ELITE':
        return 1200; // "SUV"
      default:
        return null;
    }
  }

  return null;
}

function getSuggestedDepositCurrency(location) {
  if (!location) return null;

  const loc = String(location).toUpperCase();

  if (loc === 'GE' || loc === 'GEORGIA') return 'USD';
  if (loc === 'AL' || loc === 'ALBANIA') return 'EUR';

  return null;
}

function toCompanyDateTimeInputValue(utcIso, tz) {
  if (!utcIso) return '';
  const d = new Date(utcIso);
  if (Number.isNaN(d.getTime())) return '';
  const z = toZonedTime(d, tz);
  return format(z, "yyyy-MM-dd'T'HH:mm");
}


function fromCompanyDateTimeInputValue(companyLocalValue, tz) {
  if (!companyLocalValue) return '';
  // companyLocalValue is "YYYY-MM-DDTHH:mm" (no tz)
  const utcDate = fromZonedTime(companyLocalValue, tz);
  return Number.isNaN(utcDate.getTime()) ? '' : utcDate.toISOString();
}

/* shared UI helpers */
const baseInput = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 12,
  border: '1px solid #e5e7eb',
  fontSize: 13,
  outline: 'none',
  background: '#ffffff',
  boxSizing: 'border-box',
};

function inputStyle(isMobile) {
  if (!isMobile) return baseInput;
  return {
    ...baseInput,
    fontSize: 16,
    minHeight: 48,
    padding: '12px 14px',
    borderRadius: 14,
  };
}

function touchTarget(isMobile, extra = {}) {
  if (!isMobile) return extra;
  return { minHeight: 44, minWidth: 44, touchAction: 'manipulation', ...extra };
}

const PLATFORM_OPTIONS = [
  { value: 'website', label: 'Website' },
  { value: 'discovercars', label: 'DiscoverCars' },
  { value: 'booking', label: 'Booking' },
  { value: 'local_rent', label: 'Localrent' },
  { value: 'economybookings', label: 'EconomyBookings' },
  { value: 'gateway', label: 'Gateway' },
  { value: 'private', label: 'Private' },
  { value: 'other', label: 'Other' },
];

const EXTRA_OPTIONS = [
  { value: 'booster_seat', label: 'Booster seat (4+ years)' },
  { value: 'infant_seat', label: 'Infant seat (9 months – 4 years)' },
  { value: 'baby_seat', label: 'Baby seat (0 – 9 months)' },
  { value: 'gps', label: 'GPS' },
  { value: 'snow_chains', label: 'Snow chains' },
  { value: 'bluetooth_receiver', label: 'Bluetooth receiver' },
];

/* ========== Mobile detection hook ========== */
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= breakpoint : false
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const handler = () => setIsMobile(mq.matches);
    handler();
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [breakpoint]);

  return isMobile;
}

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function getOrdinalSuffix(day) {
  if (day >= 11 && day <= 13) return 'th';
  const last = day % 10;
  if (last === 1) return 'st';
  if (last === 2) return 'nd';
  if (last === 3) return 'rd';
  return 'th';
}

/**
 * Expects "YYYY-MM-DD" and returns "January 5th 2025"
 */
function formatPrettyDate(ymd) {
  if (!ymd) return '';
  const [year, month, day] = ymd.split('-');
  const mIndex = parseInt(month, 10) - 1;
  const dNum = parseInt(day, 10);
  if (Number.isNaN(dNum) || mIndex < 0 || mIndex > 11) return ymd;

  const monthName = MONTH_NAMES[mIndex];
  const suffix = getOrdinalSuffix(dNum);
  return `${monthName} ${dNum}${suffix} ${year}`;
}

function formatPrettyDateTime(iso, tz = 'UTC') {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const z = toZonedTime(d, tz || 'UTC');

  const year = z.getFullYear();
  const monthIndex = z.getMonth(); // 0-based
  const day = z.getDate();
  const hour = String(z.getHours()).padStart(2, '0');
  const minute = String(z.getMinutes()).padStart(2, '0');

  const monthName = MONTH_NAMES[monthIndex];
  const suffix = getOrdinalSuffix(day);

  return `${monthName} ${day}${suffix} ${year}, ${hour}:${minute}`;
}

function formatPrettyTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':');
  if (!h || !m) return '';
  return `${h.padStart(2, '0')}:${m.padStart(2, '0')}`;
}

/* =============== Polished inline “design system” =============== */
const ui = {
  scrim: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(2, 6, 23, 0.55)',
    zIndex: 9998,
  },
  drawer: (isMobile) => ({
    position: 'fixed',
    top: isMobile ? 0 : 16,
    right: isMobile ? 0 : 16,
    bottom: isMobile ? 0 : 16,
    left: isMobile ? 0 : 'auto',
    width: isMobile ? '100%' : 640,
    maxWidth: isMobile ? '100vw' : 'calc(100vw - 32px)',
    height: isMobile ? '100dvh' : undefined,
    maxHeight: isMobile ? '100dvh' : undefined,
    background: '#ffffff',
    border: isMobile ? 'none' : '1px solid rgba(15,23,42,0.10)',
    borderRadius: isMobile ? 0 : 18,
    boxShadow: isMobile ? 'none' : '0 24px 80px rgba(2,6,23,0.35)',
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  }),
  topBar: (isMobile) => ({
    flexShrink: 0,
    padding: isMobile
      ? 'calc(10px + env(safe-area-inset-top, 0px)) max(14px, env(safe-area-inset-right, 0px)) 12px max(14px, env(safe-area-inset-left, 0px))'
      : '14px 16px',
    borderBottom: '1px solid #eef2f7',
    background:
      'linear-gradient(180deg, rgba(248,250,252,1) 0%, rgba(255,255,255,1) 100%)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
  }),
  title: (isMobile) => ({
    margin: 0,
    fontSize: isMobile ? 18 : 16,
    fontWeight: 900,
    color: '#0f172a',
    letterSpacing: '-0.01em',
    lineHeight: 1.15,
  }),
  sub: (isMobile) => ({
    margin: '4px 0 0',
    fontSize: isMobile ? 13 : 12,
    color: '#64748b',
    lineHeight: 1.4,
  }),
  xBtn: (isMobile) => ({
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: 999,
    padding: isMobile ? 0 : '6px 10px',
    fontSize: isMobile ? 18 : 13,
    cursor: 'pointer',
    boxShadow: '0 1px 0 rgba(15,23,42,0.04)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...touchTarget(isMobile, isMobile ? { width: 44, height: 44, flexShrink: 0 } : {}),
  }),
  tabsWrap: (isMobile) => ({
    flexShrink: 0,
    padding: isMobile
      ? '10px max(14px, env(safe-area-inset-right, 0px)) 10px max(14px, env(safe-area-inset-left, 0px))'
      : '10px 12px',
    borderBottom: '1px solid #eef2f7',
    background: '#ffffff',
  }),
  tabs: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 8,
    background: '#f1f5f9',
    padding: 6,
    borderRadius: 14,
    border: '1px solid #e2e8f0',
  },
  tabBtn: (active, isMobile) => ({
    borderRadius: 12,
    padding: isMobile ? '12px 10px' : '9px 10px',
    minHeight: isMobile ? 48 : undefined,
    fontSize: isMobile ? 14 : 13,
    fontWeight: 800,
    cursor: 'pointer',
    border: active ? '1px solid rgba(15,23,42,0.10)' : '1px solid transparent',
    background: active ? '#ffffff' : 'transparent',
    color: active ? '#0f172a' : '#475569',
    boxShadow: active ? '0 10px 20px rgba(2,6,23,0.07)' : 'none',
  }),
  body: (isMobile) => ({
    flex: 1,
    minHeight: 0,
    padding: isMobile
      ? '12px max(14px, env(safe-area-inset-right, 0px)) 16px max(14px, env(safe-area-inset-left, 0px))'
      : 14,
    overflowY: 'auto',
    overflowX: 'hidden',
    WebkitOverflowScrolling: 'touch',
    overscrollBehavior: 'contain',
    background:
      'linear-gradient(180deg, rgba(248,250,252,1) 0%, rgba(255,255,255,1) 22%)',
  }),
  statusStrip: (isMobile) => ({
    display: 'flex',
    gap: 8,
    marginBottom: 12,
    flexWrap: isMobile ? 'nowrap' : 'wrap',
    overflowX: isMobile ? 'auto' : 'visible',
    WebkitOverflowScrolling: 'touch',
    paddingBottom: isMobile ? 2 : 0,
    scrollbarWidth: 'none',
  }),
  fieldset: (readOnly) => ({
    border: 0,
    margin: 0,
    padding: 0,
    minWidth: 0,
    opacity: readOnly ? 0.75 : 1,
  }),
  card: (isMobile) => ({
    border: '1px solid #e7edf5',
    borderRadius: isMobile ? 18 : 16,
    background: '#ffffff',
    boxShadow: '0 10px 24px rgba(2,6,23,0.06)',
    overflow: 'hidden',
    marginBottom: isMobile ? 14 : 12,
  }),
  cardHead: (isMobile) => ({
    padding: isMobile ? '12px 14px' : '10px 12px',
    borderBottom: '1px solid #eef2f7',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    flexWrap: isMobile ? 'wrap' : 'nowrap',
    background: '#fbfdff',
  }),
  cardTitle: (isMobile) => ({
    fontSize: isMobile ? 13 : 12,
    fontWeight: 900,
    color: '#0f172a',
    margin: 0,
  }),
  badge: (tone) => {
    const map = {
      ok: { bg: '#dcfce7', fg: '#14532d', br: '#bbf7d0' },
      warn: { bg: '#ffedd5', fg: '#7c2d12', br: '#fed7aa' },
      bad: { bg: '#fee2e2', fg: '#7f1d1d', br: '#fecaca' },
      info: { bg: '#e0f2fe', fg: '#0c4a6e', br: '#bae6fd' },
      neutral: { bg: '#f1f5f9', fg: '#334155', br: '#e2e8f0' },
    };
    const c = map[tone] || map.neutral;
    return {
      fontSize: 11,
      fontWeight: 900,
      padding: '4px 10px',
      borderRadius: 999,
      background: c.bg,
      color: c.fg,
      border: `1px solid ${c.br}`,
      whiteSpace: 'nowrap',
      flexShrink: 0,
    };
  },
  cardBody: (isMobile) => ({ padding: isMobile ? 14 : 12 }),
  grid2: (isMobile) => ({
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
    gap: isMobile ? 14 : 10,
  }),
  label: (isMobile) => ({
    fontSize: isMobile ? 13 : 12,
    color: '#334155',
    fontWeight: 800,
    display: 'block',
    marginBottom: isMobile ? 8 : 6,
  }),
  help: (isMobile) => ({
    marginTop: isMobile ? 8 : 6,
    fontSize: isMobile ? 12 : 11,
    color: '#64748b',
    lineHeight: 1.4,
  }),
  sectionTitle: (isMobile) => ({
    margin: 0,
    fontSize: isMobile ? 14 : 12,
    fontWeight: 900,
    color: '#0f172a',
  }),
  footer: (isMobile) => ({
    flexShrink: 0,
    padding: isMobile
      ? '12px max(14px, env(safe-area-inset-right, 0px)) calc(12px + env(safe-area-inset-bottom, 0px)) max(14px, env(safe-area-inset-left, 0px))'
      : 12,
    borderTop: '1px solid #eef2f7',
    background: '#ffffff',
    boxShadow: isMobile ? '0 -8px 24px rgba(15,23,42,0.08)' : 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: isMobile ? 10 : 8,
    alignItems: 'stretch',
  }),
  footerRight: (isMobile) => ({
    display: isMobile ? 'grid' : 'flex',
    gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : undefined,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: isMobile ? 10 : 8,
    width: '100%',
    justifyContent: isMobile ? 'stretch' : 'flex-end',
    alignItems: isMobile ? 'stretch' : 'center',
  }),
  footerBtn: (isMobile) => ({
    width: isMobile ? '100%' : 'auto',
    minHeight: isMobile ? 48 : undefined,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    whiteSpace: isMobile ? 'normal' : 'nowrap',
    lineHeight: 1.2,
  }),
  footerSecondaryActions: (isMobile) => ({
    display: isMobile ? 'grid' : 'contents',
    gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : undefined,
    gap: isMobile ? 8 : undefined,
    width: '100%',
  }),
  footerPrimaryRow: (isMobile) => ({
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr 1fr' : 'auto auto',
    gap: 8,
    width: '100%',
  }),
  divider: (isMobile) => ({
    height: 1,
    background: '#eef2f7',
    margin: isMobile ? '14px 0' : '10px 0',
  }),
  prettyPickWrap: {
    position: 'relative',
    width: '100%',
  },
  prettyOverlay: (hasValue, isMobile) => ({
    position: 'absolute',
    inset: 0,
    padding: isMobile ? '12px 14px' : '10px 12px',
    fontSize: isMobile ? 16 : 13,
    display: 'flex',
    alignItems: 'center',
    pointerEvents: 'none',
    color: hasValue ? '#0f172a' : '#94a3b8',
    zIndex: 1,
  }),
  hiddenPickerInput: (isMobile) => ({
    ...inputStyle(isMobile),
    color: 'transparent',
    background: 'transparent',
    caretColor: 'transparent',
    position: 'relative',
    zIndex: 2,
  }),
  checkboxRow: (isMobile) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    padding: isMobile ? '14px' : '10px 12px',
    borderRadius: 14,
    border: '1px solid #e7edf5',
    background: '#ffffff',
    minHeight: isMobile ? 52 : undefined,
  }),
  checkboxLeft: { display: 'flex', alignItems: 'center', gap: 10 },
  checkboxLabel: (isMobile) => ({
    fontSize: isMobile ? 14 : 12,
    color: '#0f172a',
    fontWeight: 800,
  }),
  checkboxHint: (isMobile) => ({
    fontSize: isMobile ? 12 : 11,
    color: '#64748b',
    marginTop: 2,
  }),
};

function StatusPill({ children, tone = 'neutral' }) {
  return <span style={ui.badge(tone)}>{children}</span>;
}

function formatCarOptionLabel(car, occupied) {
  const name = car?.name || 'Car';
  const plate = car?.plate ? ` (${car.plate})` : '';
  const occupiedSuffix = occupied ? ' — (occupied)' : '';
  return `${name}${plate}${occupiedSuffix}`;
}

function CarSelectOption({ opt, isActive, isMobile, onPick }) {
  return (
    <button
      type="button"
      onClick={() => onPick(opt.value)}
      style={{
        width: '100%',
        textAlign: 'left',
        padding: isMobile ? '14px 16px' : '10px 12px',
        border: 'none',
        borderBottom: isMobile ? '1px solid #f1f5f9' : 'none',
        background: isActive ? '#f0fdf4' : '#ffffff',
        color: isActive ? '#166534' : '#0f172a',
        fontWeight: isActive ? 900 : 700,
        fontSize: isMobile ? 15 : 13,
        cursor: 'pointer',
        minHeight: isMobile ? 52 : undefined,
      }}
    >
      {opt.label}
    </button>
  );
}

function SearchableCarSelect({
  value,
  onChange,
  cars = [],
  occupiedIds,
  disabled = false,
  isMobile = false,
  placeholder = 'Select a car…',
  searchPlaceholder = 'Search by name, plate, or category…',
}) {
  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [pos, setPos] = useState(null);

  const field = useMemo(() => inputStyle(isMobile), [isMobile]);

  const options = useMemo(
    () =>
      (cars || []).map((c) => ({
        value: c.id,
        label: formatCarOptionLabel(c, occupiedIds?.has?.(c.id)),
        car: c,
      })),
    [cars, occupiedIds]
  );

  const selected = useMemo(
    () => options.find((o) => o.value === value) || null,
    [options, value]
  );

  const filtered = useMemo(() => {
    const nq = q.trim().toLowerCase();
    if (!nq) return options;
    return options.filter((o) => {
      const c = o.car;
      const hay = [c?.name, c?.plate, c?.category, o.label]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(nq);
    });
  }, [options, q]);

  const pick = useCallback(
    (carId) => {
      onChange(carId);
      setOpen(false);
    },
    [onChange]
  );

  const updatePos = useCallback(() => {
    if (isMobile) return;
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const margin = 12;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || r.right;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 800;
    const width = Math.min(Math.max(r.width, 280), Math.max(260, viewportWidth - margin * 2));
    const left = Math.min(
      Math.max(margin, r.left),
      Math.max(margin, viewportWidth - width - margin)
    );
    const menuMax = 320;
    const spaceBelow = viewportHeight - r.bottom - margin;
    const spaceAbove = r.top - margin;
    const openUp = spaceBelow < Math.min(menuMax, 220) && spaceAbove > spaceBelow;
    const top = openUp ? Math.max(margin, r.top - Math.min(menuMax, spaceAbove)) : r.bottom + 6;
    setPos({ left, top, width, maxHeight: openUp ? r.top - margin - top : spaceBelow - 12 });
  }, [isMobile]);

  useLayoutEffect(() => {
    if (!open || disabled || isMobile) return;
    updatePos();
  }, [open, disabled, isMobile, updatePos, q, value, options.length]);

  useEffect(() => {
    if (!open || disabled || isMobile) return;
    const onReflow = () => updatePos();
    window.addEventListener('resize', onReflow);
    window.addEventListener('scroll', onReflow, true);
    return () => {
      window.removeEventListener('resize', onReflow);
      window.removeEventListener('scroll', onReflow, true);
    };
  }, [open, disabled, isMobile, updatePos]);

  useEffect(() => {
    if (!open) return;
    function onDoc(e) {
      const w = wrapRef.current;
      const m = menuRef.current;
      if (w?.contains(e.target)) return;
      if (m?.contains(e.target)) return;
      setOpen(false);
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc, true);
    document.addEventListener('touchstart', onDoc, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc, true);
      document.removeEventListener('touchstart', onDoc, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) setQ('');
  }, [open]);


  const listContent = filtered.length ? (
    filtered.map((opt) => (
      <CarSelectOption
        key={opt.value}
        opt={opt}
        isActive={opt.value === value}
        isMobile={isMobile}
        onPick={pick}
      />
    ))
  ) : (
    <div style={{ padding: 14, fontSize: isMobile ? 14 : 13, color: '#64748b', fontWeight: 700 }}>
      No cars match your search.
    </div>
  );

  const menu =
    open && !disabled
      ? createPortal(
          isMobile ? (
            <>
              <div
                role="presentation"
                onClick={() => setOpen(false)}
                style={{
                  position: 'fixed',
                  inset: 0,
                  zIndex: 10049,
                  background: 'rgba(2, 6, 23, 0.45)',
                }}
              />
              <div
                ref={menuRef}
                role="dialog"
                aria-modal="true"
                aria-label="Select car"
                onMouseDown={(e) => e.stopPropagation()}
                style={{
                  position: 'fixed',
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 10050,
                  background: '#ffffff',
                  borderTopLeftRadius: 20,
                  borderTopRightRadius: 20,
                  boxShadow: '0 -16px 48px rgba(15,23,42,0.18)',
                  maxHeight: 'min(85dvh, 640px)',
                  display: 'flex',
                  flexDirection: 'column',
                  paddingBottom: 'env(safe-area-inset-bottom, 0px)',
                }}
              >
                <div style={{ padding: '10px 0 6px', display: 'flex', justifyContent: 'center' }}>
                  <div style={{ width: 40, height: 4, borderRadius: 999, background: '#cbd5e1' }} />
                </div>
                <div
                  style={{
                    padding: '4px 16px 12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                  }}
                >
                  <span style={{ fontSize: 16, fontWeight: 900, color: '#0f172a' }}>Select car</span>
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    style={{
                      ...touchTarget(true, {}),
                      border: '1px solid #e5e7eb',
                      borderRadius: 999,
                      background: '#fff',
                      padding: '0 14px',
                      fontSize: 13,
                      fontWeight: 800,
                      color: '#475569',
                    }}
                  >
                    Done
                  </button>
                </div>
                <div style={{ padding: '0 16px 12px' }}>
                  <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder={searchPlaceholder}
                    style={field}
                    autoFocus
                    enterKeyHint="search"
                  />
                </div>
                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  {listContent}
                </div>
              </div>
            </>
          ) : (
            pos && (
              <div
                ref={menuRef}
                onMouseDown={(e) => e.stopPropagation()}
                style={{
                  position: 'fixed',
                  left: pos.left,
                  top: pos.top,
                  width: pos.width,
                  maxHeight: pos.maxHeight,
                  zIndex: 10050,
                  border: '1px solid #e5e7eb',
                  borderRadius: 12,
                  background: '#ffffff',
                  boxShadow: '0 18px 42px rgba(15,23,42,0.14)',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div style={{ padding: 10, borderBottom: '1px solid #eef2f7', flexShrink: 0 }}>
                  <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder={searchPlaceholder}
                    style={{ ...field, height: 40, borderRadius: 10, fontSize: 13, minHeight: 40 }}
                    autoFocus
                  />
                </div>
                <div style={{ overflowY: 'auto', flex: 1 }}>{listContent}</div>
              </div>
            )
          ),
          document.body
        )
      : null;

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
        }}
        style={{
          ...field,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.65 : 1,
          textAlign: 'left',
        }}
      >
        <span
          style={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: selected ? '#0f172a' : '#94a3b8',
            fontWeight: selected ? 800 : 600,
          }}
        >
          {selected?.label || placeholder}
        </span>
        <span style={{ flexShrink: 0, fontSize: 11, color: '#64748b', lineHeight: 1 }}>
          {open ? '▴' : '▾'}
        </span>
      </button>
      {menu}
    </div>
  );
}

/* =============== ReservationDrawer Component =============== */
export default function ReservationDrawer({
  open,
  mode,
  initial,
  prefill,
  companies,
  cars,
  claims,
  useStringKeyCond,
  activeCompanyId,
  readOnly = false,
  onClose,
  onSaved,
  pushAlert,
}) {
  const navigate = useNavigate();

  // ✅ STATE FIRST
  const [tab, setTab] = useState('reservation'); // 'reservation' | 'maintenance'
  const [form, setForm] = useState(null);
  const [availableCars, setAvailableCars] = useState([]);
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [checking, setChecking] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [saving, setSaving] = useState(false);

  const occupiedIdsRef = useRef(new Set());
  const lastAvailKeyRef = useRef('');
  const savingRef = useRef(false);
  const createReservationIdRef = useRef(null);

  const isAdmin = !!(claims?.groups || []).includes('admin');
  const isCountryAdmin = !!(claims?.groups || []).includes('country_admin');
  const isPrivileged = isAdmin || isCountryAdmin;
  const isMobile = useIsMobile(); // hook after state, before memos
  const inp = useMemo(() => inputStyle(isMobile), [isMobile]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    savingRef.current = false;
    setSaving(false);
    createReservationIdRef.current =
      open && mode === 'create' ? createReadableReservationId() : null;
  }, [open, mode]);

  const pickupChecklistStatus = initial?.pickupChecklistStatus || null;
  const dropoffChecklistStatus = initial?.dropoffChecklistStatus || null;
  const pickupDateRef = useRef(null);
  const dropoffDateRef = useRef(null);
  const reservationDateRef = useRef(null);
  const pickupTimeRef = useRef(null);
  const dropoffTimeRef = useRef(null);

  const pickupLocked = useMemo(
    () =>
      mode === 'edit' &&
      tab === 'reservation' &&
      (pickupChecklistStatus || '').toUpperCase() === 'COMPLETED',
    [mode, tab, pickupChecklistStatus]
  );

  const dropoffLocked = useMemo(
    () =>
      mode === 'edit' &&
      tab === 'reservation' &&
      (dropoffChecklistStatus || '').toUpperCase() === 'COMPLETED',
    [mode, tab, dropoffChecklistStatus]
  );

  const pickupSigned = (pickupChecklistStatus || '').toUpperCase() === 'COMPLETED';
  const dropoffSigned = (dropoffChecklistStatus || '').toUpperCase() === 'COMPLETED';

  const activeCompany = useMemo(
    () => (companies || []).find((co) => co.id === form?.companyId),
    [companies, form?.companyId]
  );

  // When activeCompanyId is provided, only that company is relevant
  const visibleCompanies = useMemo(
    () =>
      activeCompanyId
        ? (companies || []).filter((co) => co.id === activeCompanyId)
        : (companies || []),
    [companies, activeCompanyId]
  );

  const yourAgencyLabel = useMemo(() => {
    const id = activeCompanyId || claims?.CompanyId;
    if (!id) return '(your agency)';
    const co = (companies || []).find((c) => c.id === id);
    return co?.name || id || '(your agency)';
  }, [companies, activeCompanyId, claims?.CompanyId]);

  const currencyOptions = activeCompany?.currencies || [];

  const companyCars = useMemo(() => {
    const cid = form?.companyId || '';
    return sortCarsByName((cars || []).filter((c) => !cid || c.CompanyId === cid));
  }, [cars, form?.companyId]);

  const selectedCar = useMemo(
    () => (cars || []).find((c) => c.id === form?.carId),
    [cars, form?.carId]
  );

  const originalReservedCarLabel = useMemo(() => {
    const name = String(form?.originalCarName || '').trim();

    if (name) return name;
    if (!selectedCar) return '';

    return `${selectedCar.name || 'Car'}${selectedCar.plate ? ` (${selectedCar.plate})` : ''}`;
  }, [form?.originalCarName, selectedCar]);

  // Try a few common field names; harmless if some are undefined
  const companyLocation =
    activeCompany?.locationCode ||
    activeCompany?.countryCode ||
    activeCompany?.country ||
    activeCompany?.location ||
    null;
  const isAlbaniaCompany = ['AL', 'ALBANIA'].includes(String(companyLocation || '').trim().toUpperCase());
  // Pick the timezone for the active company (Georgia => Asia/Tbilisi, Albania => Europe/Tirane, etc.)
  const companyTz = useMemo(() => {
    return tzForCompanyCountry(companyLocation);
  }, [companyLocation]);
  const companyTzById = useCallback((companyId) => {
    const co = (companies || []).find((x) => x.id === companyId);
    return tzForCompanyCountry(
      co?.locationCode || co?.countryCode || co?.country || co?.location
    );
  }, [companies]);

  const recommendedDeposit = useMemo(() => {
    if (!companyLocation || !selectedCar?.category) return null;
    return getSuggestedDeposit(companyLocation, selectedCar.category);
  }, [companyLocation, selectedCar?.category]);

  const suggestedDepositCurrency = useMemo(
    () => getSuggestedDepositCurrency(companyLocation),
    [companyLocation]
  );

  const pickupOptions = activeCompany?.pickupLocations || [];
  const dropoffOptions = activeCompany?.dropoffLocations || [];

  const pickupSelectValue = pickupOptions.length
    ? pickupOptions.includes(form?.pickupLocation)
      ? form.pickupLocation
      : '__OTHER__'
    : '';

  const dropoffSelectValue = dropoffOptions.length
    ? dropoffOptions.includes(form?.dropoffLocation)
      ? form.dropoffLocation
      : '__OTHER__'
    : '';

  const depositCurrencySelectValue = currencyOptions.length
    ? currencyOptions.includes(form?.depositCurrency)
      ? form.depositCurrency
      : '__OTHER__'
    : '';

  const rentalCurrencySelectValue = currencyOptions.length
    ? currencyOptions.includes(form?.rentalCurrency)
      ? form.rentalCurrency
      : '__OTHER__'
    : '';

  const insuranceCurrencySelectValue = currencyOptions.length
    ? currencyOptions.includes(form?.insuranceCurrency)
      ? form.insuranceCurrency
      : '__OTHER__'
    : '';

  useEffect(() => {
    if (!open) return;

    const nowIso = new Date().toISOString();
    const plus2hIso = new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString();

    if (mode === 'edit' && initial) {
      const initCompanyId = initial.CompanyId || claims?.CompanyId || '';
      const initTz = companyTzById(initCompanyId);
      const s = splitCompanyDateTime(initial.start || initial.pickupAt, initTz);
      const e = splitCompanyDateTime(initial.end || initial.dropoffAt, initTz);
      const parsedInitialExtras = splitInsuranceFromExtras(
        normalizeExtrasArray(initial.extras, initial.babyCarSeat)
      );
      const initialInsuranceIncluded =
        normalizeInsuranceIncludedValue(initial?.insuranceIncluded) ||
        parsedInitialExtras.insuranceIncluded;
      const initialInsuranceAmount =
        initial?.insuranceAmount != null && initial.insuranceAmount !== ''
          ? String(initial.insuranceAmount)
          : parsedInitialExtras.insuranceAmount;
      const initialInsuranceCurrency =
        String(initial?.insuranceCurrency || '').trim().toUpperCase() ||
        parsedInitialExtras.insuranceCurrency ||
        initial.rentalCurrency ||
        initial.currency ||
        '';
      const initialRentalAmount =
        initial.rentalAmount != null && initial.rentalAmount !== '' ? String(initial.rentalAmount) : '';
      const normalizedInitialPayment = normalizeIncomingPaymentState(
        initial.paymentStatus,
        initial.amountPaid != null && !Number.isNaN(initial.amountPaid) ? initial.amountPaid : '',
        initialRentalAmount
      );
      const reservationDateIso =
        toIsoStringSafe(initial.reservationDate) ||
        toIsoStringSafe(initial.start) ||
        toIsoStringSafe(initial.pickupAt) ||
        nowIso;
      const initialOriginalCarName = String(initial.originalCarName || '').trim();
      const initialOriginalMatchesAssigned =
        !!initial.originalCarId && !!initial.carId && initial.originalCarId === initial.carId;
      const shouldPrefillManualOriginal = !initialOriginalMatchesAssigned && !!initialOriginalCarName;

      setTab(initial.__kind === 'maint' ? 'maintenance' : 'reservation');
      setForm({
        id: initial.id,
        createdAt: initial.createdAt || null,
        _version: initial._version ?? null,
        companyId: initCompanyId,
        carId: initial.carId || '',
        originalCarId: initial.originalCarId || initial.carId || '',
        originalCarName: shouldPrefillManualOriginal ? initialOriginalCarName : '',
        pickupDate: s.date,
        pickupTime: s.time,
        dropoffDate: e.date,
        dropoffTime: e.time,
        title: initial.title || '',
        notes: initial.notes || '',
        status: initial.status || 'PENDING',
        reservationDate: reservationDateIso,
        reservationRef: initial.reservationRef || '',
        customerName: initial.customerName || '',
        flightNumber: initial.flightNumber || '',
        phoneNumbers: phoneNumbersToInputString(initial.phoneNumbers),
        extras: parsedInitialExtras.extras,
        extraDriversCount: parsedInitialExtras.extraDriversCount,
        insuranceIncluded: initialInsuranceIncluded,
        insuranceAmount: initialInsuranceAmount,
        insuranceCurrency: initialInsuranceCurrency,
        depositAmount: initial.depositAmount ?? '',
        rentalAmount: initialRentalAmount,
        depositCurrency: initial.depositCurrency || initial.currency || '',
        rentalCurrency: initial.rentalCurrency || initial.currency || '',
        pickupLocation: initial.pickupLocation || '',
        dropoffLocation: initial.dropoffLocation || '',
        platform: initial.platform || '',
        paymentStatus: normalizedInitialPayment.paymentStatus,
        amountPaid: normalizedInitialPayment.amountPaid,
        // email-related (hidden)
        senderEmail: initial.senderEmail || '',
        acceptUrl: initial.acceptUrl || '',
        declineUrl: initial.declineUrl || '',
      });
    } else {
      // CREATE MODE
      setTab('reservation');

      const createCompanyId = prefill?.companyId || activeCompanyId || claims?.CompanyId || '';
      const createTz = companyTzById(createCompanyId);

      const prePickupIso = toIsoStringSafe(prefill?.pickupAt) || nowIso;
      const preDropoffIso = toIsoStringSafe(prefill?.dropoffAt) || plus2hIso;
      const prePickup = splitCompanyDateTime(prePickupIso, createTz);
      const preDropoff = splitCompanyDateTime(preDropoffIso, createTz);
      const reservationDateIso = toIsoStringSafe(prefill?.reservationDate) || nowIso;
      const parsedPrefillExtras = splitInsuranceFromExtras(
        normalizeExtrasArray(prefill?.extras, prefill?.babyCarSeat)
      );
      const prefillInsuranceIncluded =
        normalizeInsuranceIncludedValue(prefill?.insuranceIncluded) ||
        parsedPrefillExtras.insuranceIncluded;
      const prefillInsuranceAmount =
        prefill && prefill.insuranceAmount != null && prefill.insuranceAmount !== ''
          ? String(prefill.insuranceAmount)
          : parsedPrefillExtras.insuranceAmount;
      const prefillInsuranceCurrency =
        String(prefill?.insuranceCurrency || '').trim().toUpperCase() ||
        parsedPrefillExtras.insuranceCurrency ||
        prefill?.rentalCurrency ||
        '';
      const prefillRentalAmount =
        prefill?.rentalAmount != null && prefill.rentalAmount !== '' ? String(prefill.rentalAmount) : '';
      const normalizedPrefillPayment = normalizeIncomingPaymentState(
        prefill?.paymentStatus,
        prefill && prefill.amountPaid != null ? prefill.amountPaid : '',
        prefillRentalAmount
      );
      const prefillOriginalCarName = String(prefill?.originalCarName || '').trim();
      const prefillOriginalMatchesAssigned =
        !!prefill?.originalCarId && !!prefill?.carId && prefill.originalCarId === prefill.carId;
      const shouldPrefillManualOriginal = !prefillOriginalMatchesAssigned && !!prefillOriginalCarName;

      const phoneStr = phoneNumbersToInputString(prefill?.phoneNumbers);

      setForm({
        id: null,
        _version: null,
        companyId: createCompanyId,
        carId: prefill?.carId || '',
        originalCarId: prefill?.originalCarId || prefill?.carId || '',
        originalCarName: shouldPrefillManualOriginal ? prefillOriginalCarName : '',
        pickupDate: prePickup.date,
        pickupTime: prePickup.time,
        dropoffDate: preDropoff.date,
        dropoffTime: preDropoff.time,
        title: prefill?.title || '',
        notes: prefill?.notes || '',
        status: 'PENDING',
        reservationDate: reservationDateIso,
        reservationRef: prefill?.reservationRef || '',
        customerName: prefill?.customerName || '',
        flightNumber: prefill?.flightNumber || '',
        phoneNumbers: phoneStr,
        extras: parsedPrefillExtras.extras,
        extraDriversCount: parsedPrefillExtras.extraDriversCount,
        insuranceIncluded: prefillInsuranceIncluded,
        insuranceAmount: prefillInsuranceAmount,
        insuranceCurrency: prefillInsuranceCurrency,

        depositAmount: prefill?.depositAmount != null ? String(prefill.depositAmount) : '',
        rentalAmount: prefillRentalAmount,
        depositCurrency: prefill?.depositCurrency || '',
        rentalCurrency: prefill?.rentalCurrency || '',
        pickupLocation: prefill?.pickupLocation || '',
        dropoffLocation: prefill?.dropoffLocation || '',
        platform: prefill?.platform || '',
        // payment
        paymentStatus: normalizedPrefillPayment.paymentStatus,
        amountPaid: normalizedPrefillPayment.amountPaid,
        // email-related (hidden)
        senderEmail: prefill?.senderEmail || '',
        acceptUrl: prefill?.acceptUrl || '',
        declineUrl: prefill?.declineUrl || '',
      });
    }

    setAvailableCars([]);
    setConflicts([]);
    lastAvailKeyRef.current = '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, initial, prefill, claims?.CompanyId, activeCompanyId]);

  const pickupAt = form ? toUtcIsoFromCompanyParts(form.pickupDate, form.pickupTime, companyTz) : null;
  const dropoffAt = form ? toUtcIsoFromCompanyParts(form.dropoffDate, form.dropoffTime, companyTz) : null;

  const validWindow = !!(pickupAt && dropoffAt && pickupAt < dropoffAt);

  const baseCars = useMemo(() => {
    return companyCars.filter((c) => c.active);
  }, [companyCars]);

  function dedupeCars(list) {
    const seen = new Set();
    const out = [];
    for (const c of list) {
      if (!c) continue;
      if (seen.has(c.id)) continue;
      seen.add(c.id);
      out.push(c);
    }
    return out;
  }

  // Availability: reservations + maintenance overlaps, exclude self for edits
  useEffect(() => {
    let cancelled = false;
    async function loadAvail() {
      if (!open) return;

      const cid = form?.companyId || '';
      const sIso = pickupAt || '';
      const eIso = dropoffAt || '';
      const k = `${cid}|${sIso}|${eIso}|${useStringKeyCond ? 'S' : 'D'}|${mode}`;

      if (!cid) {
        if (availableCars.length) setAvailableCars([]);
        if (mode !== 'edit' && form?.carId) setForm((f) => ({ ...f, carId: f.carId }));
        lastAvailKeyRef.current = k;
        return;
      }

      if (!validWindow) {
        const all = baseCars.slice();
        if (mode === 'edit' && form?.carId) {
          const cur = (cars || []).find((c) => c.id === form.carId);
          if (cur && cur.active && (!cid || cur.CompanyId === cid)) all.unshift(cur);
        }
        const deduped = dedupeCars(all);
        setAvailableCars(sortCarsByName(deduped));
        lastAvailKeyRef.current = k;
        return;
      }

      if (lastAvailKeyRef.current === k) return;
      lastAvailKeyRef.current = k;

      setLoadingAvail(true);
      try {
        const resQuery = useStringKeyCond ? ByCompanyByStart_STR : ByCompanyByStart_DT;
        const mtQuery = useStringKeyCond ? MaintByCompanyByStart_STR : MaintByCompanyByStart_DT;
        const startCond = overlapKey(eIso);

        const [res, mt] = await Promise.all([
          client.graphql({
            query: resQuery,
            variables: { CompanyId: cid, start: startCond },
            authMode: 'userPool',
          }),
          client.graphql({
            query: mtQuery,
            variables: { CompanyId: cid, start: startCond },
            authMode: 'userPool',
          }),
        ]);
        if (cancelled) return;

        const resItems = (res?.data?.reservationsByCompanyByStart?.items || [])
          .filter((r) => r && isBlocking(r) && (mode !== 'edit' || r.id !== form?.id));
        const mtItems = (mt?.data?.maintenanceByCompanyByStart?.items || [])
          .filter((m) => m && isBlocking(m) && (mode !== 'edit' || m.id !== form?.id));

        const occupied = new Set([
          ...resItems.flatMap((r) =>
            getReservationCarSegments(r)
              .filter(
                (segment) =>
                  segment.carId && overlapsString(sIso, eIso, segment.start, segment.end)
              )
              .map((segment) => segment.carId)
          ),
          ...mtItems
            .filter((m) => m.carId && overlapsString(sIso, eIso, m.start, m.end))
            .map((m) => m.carId),
        ]);
        occupiedIdsRef.current = occupied;

        let list = baseCars.filter((c) => !occupied.has(c.id));
        if (mode === 'edit' && form?.carId) {
          const current = (cars || []).find((c) => c.id === form.carId);
          if (current && current.active && (!cid || current.CompanyId === cid)) list = [current, ...list];
        }
        const deduped = dedupeCars(list);
        setAvailableCars(sortCarsByName(deduped));

        if (mode !== 'edit' && form?.carId && !list.find((c) => c.id === form.carId)) {
          setForm((f) => ({ ...f, carId: '' }));
        }
      } catch (err) {
        console.error('Availability load failed:', err);
        let list = baseCars.slice();
        if (mode === 'edit' && form?.carId) {
          const current = (cars || []).find((c) => c.id === form.carId);
          if (current && current.active && (!form.companyId || current.CompanyId === form.companyId)) {
            list = [current, ...list];
          }
        }
        const deduped = dedupeCars(list);
        setAvailableCars(sortCarsByName(deduped));
      } finally {
        if (!cancelled) setLoadingAvail(false);
      }
    }
    loadAvail();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    mode,
    cars,
    form?.id,
    form?.companyId,
    form?.carId,
    form?.pickupDate,
    form?.pickupTime,
    form?.dropoffDate,
    form?.dropoffTime,
    validWindow,
    useStringKeyCond,
  ]);

  // Per-car conflict check
  async function checkCar(carId, startIso, endIso, silent = false) {
    setChecking(true);
    try {
      const resQuery = useStringKeyCond ? ByCompanyByStart_STR : ByCompanyByStart_DT;
      const mtQuery = useStringKeyCond ? MaintByCompanyByStart_STR : MaintByCompanyByStart_DT;
      const startCond = overlapKey(endIso);
      const companyId = form?.companyId || '';

      if (!companyId) {
        setConflicts([{ id: 'missing-company' }]);
        return [{ id: 'missing-company' }];
      }

      const [res, mt] = await Promise.all([
        client.graphql({
          query: resQuery,
          variables: { CompanyId: companyId, start: startCond },
          authMode: 'userPool',
        }),
        client.graphql({
          query: mtQuery,
          variables: { CompanyId: companyId, start: startCond },
          authMode: 'userPool',
        }),
      ]);

      const resItems = (res?.data?.reservationsByCompanyByStart?.items || [])
        .filter((r) => r && isBlocking(r) && (mode !== 'edit' || r.id !== form?.id));
      const mtItems = (mt?.data?.maintenanceByCompanyByStart?.items || [])
        .filter((m) => m && isBlocking(m) && (mode !== 'edit' || m.id !== form?.id));

      const hitRes = resItems.filter((r) =>
        reservationUsesCarInWindow(r, carId, startIso, endIso)
      );
      const hitMt = mtItems.filter(
        (m) => m.carId === carId && overlapsString(startIso, endIso, m.start, m.end)
      );

      const hits = [...hitRes, ...hitMt];
      setConflicts(hits);

      if (!silent && pushAlert) {
        if (hits.length) {
          pushAlert({
            type: 'warning',
            title: 'Conflicts detected',
            message: `This window conflicts with ${hits.length} existing item(s).`,
          });
        } else {
          pushAlert({
            type: 'success',
            title: 'No conflicts',
            message: 'This car is free in that window.',
          });
        }
      }

      return hits;
    } catch (e) {
      console.error('checkCar failed:', e);
      setConflicts([{ id: 'err' }]);
      if (pushAlert) {
        pushAlert({
          type: 'error',
          title: 'Availability check failed',
          message: 'Could not check availability. See console for details.',
        });
      }
      return [{ id: 'err' }];
    } finally {
      setChecking(false);
    }
  }

  async function save() {
    if (readOnly) return;
    if (!form) return;

    if (!form.companyId) {
      pushAlert?.({ type: 'error', title: 'Missing agency', message: 'Pick an agency before saving.' });
      return;
    }
    if (!validWindow) {
      pushAlert?.({ type: 'error', title: 'Invalid time window', message: 'Set a valid pickup and dropoff date/time.' });
      return;
    }
    if (!form.carId) {
      pushAlert?.({ type: 'error', title: 'No car selected', message: 'Pick a car before saving.' });
      return;
    }

    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);

    const startIso = pickupAt;
    const endIso = dropoffAt;

    try {
      const hits = await checkCar(form.carId, startIso, endIso, true);

      if (hits.length && pushAlert) {
        savingRef.current = false;
        setSaving(false);
        pushAlert({
          type: 'confirm',
          title: 'Conflicts found',
          message: `This window conflicts with ${hits.length} existing item(s). Do you want to save anyway?`,
          confirmLabel: 'Save anyway',
          cancelLabel: 'Cancel',
          onConfirm: async () => {
            if (savingRef.current) return;
            savingRef.current = true;
            setSaving(true);
            try {
              await performSave(startIso, endIso);
            } finally {
              savingRef.current = false;
              setSaving(false);
            }
          },
        });
        return;
      }

      await performSave(startIso, endIso);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }

  async function performSave(startIso, endIso) {
    let savedId = mode === 'edit' ? form.id : null;
    let lastReservationBase = null;

    try {
      if (tab === 'maintenance') {
        // ===== MAINTENANCE =====
        const base = {
          CompanyId: form.companyId,
          carId: form.carId,
          title: (form.title || '').trim() || 'Maintenance',
          start: startIso,
          end: endIso,
          notes: (form.notes || '').slice(0, 2000) || null,
        };

        if (isPrivileged) {
          // Admin mutates live
          if (mode === 'create') {
            const res = await client.graphql({
              query: CreateMaintenance,
              variables: { input: { ...base, approvalStatus: 'APPROVED' } },
              authMode: 'userPool',
            });
            savedId = res?.data?.createMaintenance?.id || savedId;
          } else {
            const res = await client.graphql({
              query: UpdateMaintenance,
              variables: {
                input: {
                  id: form.id,
                  ...base,
                  approvalStatus: 'APPROVED',
                  createdAt: form.createdAt,
                  ...(form._version != null ? { _version: form._version } : {}),
                },
              },
              authMode: 'userPool',
            });
            savedId = res?.data?.updateMaintenance?.id || savedId;
          }
        } else {
          if (mode === 'create') {
            // Create PENDING live so calendar blocks the slot
            const cr = await client.graphql({
              query: CreateMaintenance,
              variables: { input: { ...base, approvalStatus: 'PENDING' } },
              authMode: 'userPool',
            });
            const newId = cr?.data?.createMaintenance?.id;
            savedId = newId || savedId;

            const cleanPayload = sanitizeJson({ approvalStatus: 'APPROVED' });
            await client.graphql({
              query: ProposeChange,
              variables: {
                input: {
                  CompanyId: form.companyId,
                  entity: 'MAINTENANCE',
                  op: 'UPDATE',
                  targetId: newId,
                  payload: JSON.stringify(cleanPayload),
                },
              },
              authMode: 'userPool',
            });
          } else {
            // EDIT: propagate change live as PENDING, approval just flips status
            const updateInput = {
              id: form.id,
              ...base,
              approvalStatus: 'PENDING',
              createdAt: form.createdAt,
              ...(form._version != null ? { _version: form._version } : {}),
            };
            const res = await client.graphql({
              query: UpdateMaintenance,
              variables: { input: updateInput },
              authMode: 'userPool',
            });
            savedId = res?.data?.updateMaintenance?.id || savedId;

            const cleanPayload = sanitizeJson({ approvalStatus: 'APPROVED' });
            await client.graphql({
              query: ProposeChange,
              variables: {
                input: {
                  CompanyId: form.companyId,
                  entity: 'MAINTENANCE',
                  op: 'UPDATE',
                  targetId: form.id,
                  payload: JSON.stringify(cleanPayload),
                },
              },
              authMode: 'userPool',
            });
          }
        }
      } else {
        // ===== RESERVATION =====
        // Keep UI as comma-separated string, but store as AWSJSON (stringified JSON)
        const phoneNumbersAwsJson = phoneNumbersToAwsJson(form.phoneNumbers);
        if (mode === 'create' && !createReservationIdRef.current) {
          createReservationIdRef.current = createReadableReservationId();
        }
        const generatedReservationId =
          mode === 'create' ? createReservationIdRef.current : null;
        const effectiveReservationId = mode === 'create' ? generatedReservationId : form.id;
        const effectiveReservationRef =
          (form.reservationRef || '').trim() || effectiveReservationId || null;

        if (mode === 'create' && effectiveReservationId) {
          savedId = effectiveReservationId;
        }

        const rawExtrasForSave = withInsuranceExtras(
          form.extras,
          form.insuranceIncluded,
          form.insuranceAmount,
          form.insuranceCurrency,
          form.extraDriversCount
        );
        const crossBorderCountryTokens = rawExtrasForSave.filter((token) =>
          String(token || '').trim().toLowerCase().startsWith(CROSS_BORDER_COUNTRY_TOKEN_PREFIX)
        );
        const baseExtrasForSave = rawExtrasForSave.filter((token) => {
          const t = String(token || '').trim().toLowerCase();
          return t !== 'cross_border' && !t.startsWith(CROSS_BORDER_COUNTRY_TOKEN_PREFIX);
        });
        const extrasForSave =
          isAlbaniaCompany && crossBorderCountryTokens.length
            ? Array.from(new Set([...baseExtrasForSave, 'cross_border', ...crossBorderCountryTokens]))
            : baseExtrasForSave;
        const manualOriginalCarName = String(form.originalCarName || '').trim();
        const hasManualOriginalCar = !!manualOriginalCarName;
        const assignedCarForSave = (cars || []).find((c) => c.id === form.carId) || null;

        const base = {
          ...(mode === 'create' && effectiveReservationId ? { id: effectiveReservationId } : {}),
          CompanyId: form.companyId,
          carId: form.carId,
          originalCarId: hasManualOriginalCar ? null : form.carId || null,
          originalCarName: hasManualOriginalCar
            ? manualOriginalCarName || null
            : assignedCarForSave?.name || null,
          originalCarPlate: hasManualOriginalCar ? null : assignedCarForSave?.plate || null,
          status: form.status || 'PENDING',
          title: (form.title || '').trim() || 'Reservation',
          start: startIso,
          end: endIso,
          pickupAt: startIso,
          dropoffAt: endIso,
          pickupLocation: form.pickupLocation || null,
          dropoffLocation: form.dropoffLocation || null,
          reservationDate: form.reservationDate || new Date().toISOString(),
          reservationRef: effectiveReservationRef,
          customerName: form.customerName || null,
          flightNumber: form.flightNumber || null,
          phoneNumbers: phoneNumbersAwsJson,
          extras: extrasToAwsJson(extrasForSave),
          depositAmount: form.depositAmount === '' ? null : parseFloat(form.depositAmount),
          rentalAmount: form.rentalAmount === '' ? null : parseFloat(form.rentalAmount),
          depositCurrency: form.depositCurrency || null,
          rentalCurrency: form.rentalCurrency || null,
          platform: form.platform || null,
          // email-related extras
          senderEmail: form.senderEmail || null,
          acceptUrl: form.acceptUrl || null,
          declineUrl: form.declineUrl || null,
          notes: (form.notes || '').slice(0, 2000) || null,
          // payment
          paymentStatus: form.paymentStatus || 'NOT_PAID',
          amountPaid:
            form.amountPaid === '' || form.amountPaid == null ? null : parseFloat(form.amountPaid),
        };

        lastReservationBase = base;

        if (isPrivileged) {
          if (mode === 'create') {
            const res = await client.graphql({
              query: CreateReservation,
              variables: { input: { ...base, approvalStatus: 'APPROVED' } },
              authMode: 'userPool',
            });
            savedId = res?.data?.createReservation?.id || savedId;
          } else {
            const res = await client.graphql({
              query: UpdateReservation,
              variables: {
                input: {
                  id: form.id,
                  ...base,
                  approvalStatus: 'APPROVED',
                  createdAt: form.createdAt,
                  ...(form._version != null ? { _version: form._version } : {}),
                },
              },
              authMode: 'userPool',
            });
            savedId = res?.data?.updateReservation?.id || savedId;
          }
        } else {
          if (mode === 'create') {
            // Create PENDING live (to block availability)
            const cr = await client.graphql({
              query: CreateReservation,
              variables: { input: { ...base, approvalStatus: 'PENDING' } },
              authMode: 'userPool',
            });
            const newId = cr?.data?.createReservation?.id;
            savedId = newId || savedId;

            const cleanPayload = sanitizeJson({ approvalStatus: 'APPROVED' });
            await client.graphql({
              query: ProposeChange,
              variables: {
                input: {
                  CompanyId: form.companyId,
                  entity: 'RESERVATION',
                  op: 'UPDATE',
                  targetId: newId,
                  payload: JSON.stringify(cleanPayload),
                },
              },
              authMode: 'userPool',
            });
          } else {
            // EDIT: propagate change live as PENDING, approval just flips status
            const updateInput = {
              id: form.id,
              ...base,
              approvalStatus: 'PENDING',
              createdAt: form.createdAt,
              ...(form._version != null ? { _version: form._version } : {}),
            };
            const res = await client.graphql({
              query: UpdateReservation,
              variables: { input: updateInput },
              authMode: 'userPool',
            });
            savedId = res?.data?.updateReservation?.id || savedId;

            const cleanPayload = sanitizeJson({ approvalStatus: 'APPROVED' });
            await client.graphql({
              query: ProposeChange,
              variables: {
                input: {
                  CompanyId: form.companyId,
                  entity: 'RESERVATION',
                  op: 'UPDATE',
                  targetId: form.id,
                  payload: JSON.stringify(cleanPayload),
                },
              },
              authMode: 'userPool',
            });
          }
        }
      }

      const info =
        tab === 'maintenance'
          ? { kind: 'maintenance', mode, id: savedId, companyId: form.companyId, carId: form.carId, start: startIso, end: endIso }
          : { kind: 'reservation', mode, id: savedId, companyId: form.companyId, carId: form.carId, start: startIso, end: endIso, platform: lastReservationBase?.platform || null };

      pushAlert?.({
        type: 'success',
        title: mode === 'create' ? 'Created' : 'Updated',
        message: tab === 'maintenance' ? 'Maintenance block saved successfully.' : 'Reservation saved successfully.',
      });

      onSaved?.(info);
      onClose?.();
    } catch (e) {
      console.error('save failed:', e);
      pushAlert?.({ type: 'error', title: 'Save failed', message: 'Failed to save. Check console for details.' });
    }
  }
  const [deleting, setDeleting] = useState(false);

  async function doDelete() {
    if (readOnly) return;
    if (mode !== 'edit') return;
    if (!form?.id) return;

    const isRes = tab === 'reservation';
    const label = isRes ? 'reservation' : 'maintenance block';

    // Optional: prevent accidental deletion of signed handovers
    // (remove this guard if you *do* want to allow it)
    if (isRes && (pickupSigned || dropoffSigned)) {
      pushAlert?.({
        type: 'confirm',
        title: 'Delete signed reservation?',
        message:
          'Pickup and/or dropoff handover is signed. Deleting will remove the reservation record. Continue?',
        confirmLabel: 'Delete anyway',
        cancelLabel: 'Cancel',
        onConfirm: async () => {
          await doDeleteConfirmed();
        },
      });
      return;
    }

    pushAlert?.({
      type: 'confirm',
      title: `Delete ${label}?`,
      message: `This will permanently delete this ${label}. This cannot be undone.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      onConfirm: async () => {
        await doDeleteConfirmed();
      },
    });
  }

  async function doDeleteConfirmed() {
    if (deleting) return;
    setDeleting(true);

    try {
      const input = {
        id: form.id,
        ...(form._version != null ? { _version: form._version } : {}),
      };

      if (tab === 'maintenance') {
        await client.graphql({
          query: DeleteMaintenance,
          variables: { input },
          authMode: 'userPool',
        });
      } else {
        await client.graphql({
          query: DeleteReservation,
          variables: { input },
          authMode: 'userPool',
        });
      }

      pushAlert?.({
        type: 'success',
        title: 'Deleted',
        message: tab === 'maintenance' ? 'Maintenance block deleted.' : 'Reservation deleted.',
      });

      onSaved?.({
        kind: tab === 'maintenance' ? 'maintenance' : 'reservation',
        mode: 'delete',
        id: form.id,
        companyId: form.companyId,
        carId: form.carId,
      });

      onClose?.();
    } catch (e) {
      console.error('delete failed:', e);
      pushAlert?.({
        type: 'error',
        title: 'Delete failed',
        message: 'Failed to delete. Check console for details.',
      });
    } finally {
      setDeleting(false);
    }
  }

  if (!open || !form) return null;

  const rentalAmountNum =
    form.rentalAmount === '' || form.rentalAmount == null ? NaN : parseFloat(form.rentalAmount);

  const amountPaidNum =
    form.amountPaid === '' || form.amountPaid == null ? NaN : parseFloat(form.amountPaid);

  const remainingAmountNum =
    Number.isNaN(rentalAmountNum) || Number.isNaN(amountPaidNum)
      ? null
      : Math.max(0, rentalAmountNum - amountPaidNum);

  const conflictTone =
    checking ? 'info' : conflicts.length ? 'bad' : validWindow ? 'ok' : 'warn';

  const conflictText = checking
    ? 'Checking availability…'
    : conflicts.length
      ? `Conflicts: ${conflicts.length}`
      : validWindow
        ? 'No conflicts detected'
        : 'Pick a valid time window';

  const toggleExtra = (value) => {
    setForm((f) => {
      const current = Array.isArray(f.extras) ? f.extras : [];
      if (current.includes(value)) return { ...f, extras: current.filter((v) => v !== value) };
      return { ...f, extras: [...current, value] };
    });
  };

  const setSeatCount = (value, count) => {
    setForm((f) => ({
      ...f,
      extras: withSeatExtraCount(f.extras, value, count),
    }));
  };

  const toggleCrossBorderCountry = (countryName) => {
    const token = crossBorderCountryToken(countryName);
    if (!token) return;
    setForm((f) => {
      const current = Array.isArray(f.extras) ? f.extras : [];
      const hasToken = current.includes(token);
      let next = hasToken ? current.filter((v) => v !== token) : [...current, token];
      const hasCountryTokens = next.some((v) =>
        String(v || '').trim().toLowerCase().startsWith(CROSS_BORDER_COUNTRY_TOKEN_PREFIX)
      );
      next = next.filter((v) => String(v || '').trim().toLowerCase() !== 'cross_border');
      if (hasCountryTokens) next.unshift('cross_border');
      return { ...f, extras: Array.from(new Set(next)) };
    });
  };

  return (
    <>
      <div style={ui.scrim} onClick={onClose} />
      <div
        style={ui.drawer(isMobile)}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        {/* TOP BAR */}
        <div style={ui.topBar(isMobile)}>
          <div style={{ minWidth: 0 }}>
            <h3 style={ui.title(isMobile)}>
              {mode === 'edit'
                ? tab === 'maintenance'
                  ? 'Edit Maintenance'
                  : 'Edit Reservation'
                : tab === 'maintenance'
                  ? 'New Maintenance'
                  : 'New Reservation'}
            </h3>
            <p style={ui.sub(isMobile)}>
              {tab === 'maintenance'
                ? 'Block a car for service, cleaning, repairs, or internal use.'
                : 'Create or update a reservation. Availability is checked against approved/pending items.'}
            </p>
          </div>

          <button type="button" onClick={onClose} style={ui.xBtn(isMobile)} aria-label="Close">
            ✕
          </button>
        </div>

        {/* TABS */}
        <div style={ui.tabsWrap(isMobile)}>
          <div style={ui.tabs}>
            <button
              type="button"
              onClick={() => setTab('reservation')}
              style={ui.tabBtn(tab === 'reservation', isMobile)}
              disabled={readOnly}
            >
              Reservation
            </button>
            <button
              type="button"
              onClick={() => setTab('maintenance')}
              style={ui.tabBtn(tab === 'maintenance', isMobile)}
              disabled={readOnly}
            >
              Maintenance
            </button>
          </div>
        </div>

        {/* BODY */}
        <div style={ui.body(isMobile)}>
          <fieldset disabled={readOnly} style={ui.fieldset(readOnly)}>
            {/* QUICK STATUS STRIP */}
            <div style={ui.statusStrip(isMobile)}>
              <StatusPill tone={conflictTone}>{conflictText}</StatusPill>
              {tab === 'reservation' && (
                <>
                  <StatusPill tone={pickupSigned ? 'ok' : 'neutral'}>
                    Pickup: {pickupSigned ? 'Signed' : 'Not signed'}
                  </StatusPill>
                  <StatusPill tone={dropoffSigned ? 'ok' : 'neutral'}>
                    Dropoff: {dropoffSigned ? 'Signed' : 'Not signed'}
                  </StatusPill>
                </>
              )}
              {loadingAvail && <StatusPill tone="info">Loading cars…</StatusPill>}
            </div>

            {/* CARD: CORE */}
            <div style={ui.card(isMobile)}>
              <div style={ui.cardHead(isMobile)}>
                <p style={ui.cardTitle(isMobile)}>Core</p>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {mode === 'edit' && form?.id ? (
                    <span style={ui.badge('neutral')}>ID: {String(form.id).slice(0, 8)}…</span>
                  ) : (
                    <span style={ui.badge('neutral')}>New</span>
                  )}
                </div>
              </div>

              <div style={ui.cardBody(isMobile)}>
                {/* Agency + Cars */}
                <div style={ui.grid2(isMobile)}>
                  <label>
                    <span style={ui.label(isMobile)}>Agency</span>
                    {(() => {
                      const companySelectDisabled = !!activeCompanyId || !isAdmin;
                      return (
                        <select
                          style={inp}
                          value={form.companyId}
                          onChange={(e) =>
                            setForm((f) => ({ ...f, companyId: e.target.value, carId: f.carId }))
                          }
                          disabled={companySelectDisabled}
                        >
                          <option value="">{companySelectDisabled ? yourAgencyLabel : 'Select agency'}</option>
                          {visibleCompanies.map((co) => (
                            <option key={co.id} value={co.id}>
                              {co.name}
                            </option>
                          ))}
                        </select>
                      );
                    })()}
                    <div style={ui.help(isMobile)}>
                      {activeCompany?.name ? `Active: ${activeCompany.name}` : ' '}
                    </div>
                  </label>

                  <label>
                    <span style={ui.label(isMobile)}>Assigned car</span>
                    <SearchableCarSelect
                      value={form.carId}
                      onChange={(carId) => setForm({ ...form, carId })}
                      cars={availableCars}
                      occupiedIds={occupiedIdsRef.current}
                      isMobile={isMobile}
                      disabled={!form.companyId || loadingAvail || pickupLocked || dropoffLocked}
                      placeholder={
                        loadingAvail
                          ? 'Checking…'
                          : !form.companyId
                            ? 'Select agency'
                            : 'Select a car…'
                      }
                    />
                    <div style={ui.help(isMobile)}>
                      {selectedCar?.category ? `Category: ${selectedCar.category}` : ' '}
                    </div>
                  </label>
                </div>

                <div style={{ marginTop: 10, ...ui.grid2(isMobile) }}>
                  <label>
                    <span style={ui.label(isMobile)}>Original reserved car</span>
                    <input
                      style={inp}
                      value={form.originalCarName || ''}
                      onChange={(e) => setForm({ ...form, originalCarName: e.target.value })}
                      placeholder="Leave blank to use assigned car"
                    />
                  </label>
                </div>
                <div style={{ ...ui.help(isMobile), marginTop: 8 }}>
                  {originalReservedCarLabel
                    ? `Original booking: ${originalReservedCarLabel}`
                    : 'Leave this blank to default to the currently assigned car.'}
                </div>

                <div style={ui.divider(isMobile)} />

                {/* Window */}
                <p style={ui.sectionTitle(isMobile)}>
                  Time window
                </p>
                <div style={{ marginTop: 10, ...ui.grid2(isMobile) }}>
                  <label>
                    <span style={ui.label(isMobile)}>Start date</span>
                    <div
                      style={ui.prettyPickWrap}
                      onClick={() => {
                        pickupDateRef.current?.showPicker?.();
                        pickupDateRef.current?.focus();
                      }}
                    >
                      <input
                        ref={pickupDateRef}
                        type="date"
                        lang="en-GB"
                        style={ui.hiddenPickerInput(isMobile)}
                        value={form.pickupDate}
                        onChange={(e) =>
                          setForm((f) => {
                            const newPickup = e.target.value;
                            let newDrop = f.dropoffDate;
                            if (newDrop && newDrop < newPickup) newDrop = newPickup;
                            return { ...f, pickupDate: newPickup, dropoffDate: newDrop };
                          })
                        }
                        disabled={pickupLocked}
                      />
                      <div style={ui.prettyOverlay(!!form.pickupDate, isMobile)}>
                        {form.pickupDate ? formatPrettyDate(form.pickupDate) : 'Select date…'}
                      </div>
                    </div>
                  </label>

                  <label>
                    <span style={ui.label(isMobile)}>Start time</span>
                    <div
                      style={ui.prettyPickWrap}
                      onClick={() => {
                        pickupTimeRef.current?.showPicker?.();
                        pickupTimeRef.current?.focus();
                      }}
                    >
                      <input
                        ref={pickupTimeRef}
                        type="time"
                        lang="en-GB"
                        style={ui.hiddenPickerInput(isMobile)}
                        value={form.pickupTime}
                        onChange={(e) => setForm({ ...form, pickupTime: e.target.value })}
                        disabled={pickupLocked}
                      />
                      <div style={ui.prettyOverlay(!!form.pickupTime, isMobile)}>
                        {form.pickupTime ? formatPrettyTime(form.pickupTime) : 'Select time…'}
                      </div>
                    </div>
                  </label>
                </div>

                <div style={{ marginTop: 10, ...ui.grid2(isMobile) }}>
                  <label>
                    <span style={ui.label(isMobile)}>End date</span>
                    <div
                      style={ui.prettyPickWrap}
                      onClick={() => {
                        dropoffDateRef.current?.showPicker?.();
                        dropoffDateRef.current?.focus();
                      }}
                    >
                      <input
                        ref={dropoffDateRef}
                        type="date"
                        lang="en-GB"
                        style={ui.hiddenPickerInput(isMobile)}
                        value={form.dropoffDate}
                        min={form.pickupDate || undefined}
                        onChange={(e) =>
                          setForm((f) => {
                            const raw = e.target.value;
                            if (f.pickupDate && raw && raw < f.pickupDate) {
                              return { ...f, dropoffDate: f.pickupDate };
                            }
                            return { ...f, dropoffDate: raw };
                          })
                        }
                        disabled={dropoffLocked}
                      />
                      <div style={ui.prettyOverlay(!!form.dropoffDate, isMobile)}>
                        {form.dropoffDate ? formatPrettyDate(form.dropoffDate) : 'Select date…'}
                      </div>
                    </div>
                  </label>

                  <label>
                    <span style={ui.label(isMobile)}>End time</span>
                    <div
                      style={ui.prettyPickWrap}
                      onClick={() => {
                        dropoffTimeRef.current?.showPicker?.();
                        dropoffTimeRef.current?.focus();
                      }}
                    >
                      <input
                        ref={dropoffTimeRef}
                        type="time"
                        lang="en-GB"
                        style={ui.hiddenPickerInput(isMobile)}
                        value={form.dropoffTime}
                        onChange={(e) => setForm({ ...form, dropoffTime: e.target.value })}
                        disabled={dropoffLocked}
                      />
                      <div style={ui.prettyOverlay(!!form.dropoffTime, isMobile)}>
                        {form.dropoffTime ? formatPrettyTime(form.dropoffTime) : 'Select time…'}
                      </div>
                    </div>
                  </label>
                </div>

                {tab === 'reservation' && (pickupLocked || dropoffLocked) && (
                  <div style={ui.help(isMobile)}>
                    {pickupLocked && 'Pickup handover signed – start time and car are locked. '}
                    {dropoffLocked && 'Dropoff handover signed – end time is locked.'}
                  </div>
                )}
              </div>
            </div>

            {/* CARD: DETAILS */}
            <div style={ui.card(isMobile)}>
              <div style={ui.cardHead(isMobile)}>
                <p style={ui.cardTitle(isMobile)}>Details</p>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {tab === 'maintenance' ? (
                    <span style={ui.badge('neutral')}>Block</span>
                  ) : (
                    <span style={ui.badge('neutral')}>Reservation</span>
                  )}
                </div>
              </div>

              <div style={ui.cardBody(isMobile)}>
                <div style={ui.grid2(isMobile)}>
                  <label>
                    <span style={ui.label(isMobile)}>Title</span>
                    <input
                      style={inp}
                      value={form.title}
                      onChange={(e) => setForm({ ...form, title: e.target.value })}
                    />
                  </label>

                  {tab === 'reservation' ? (
                    <label>
                      <span style={ui.label(isMobile)}>Source / platform</span>
                      <select
                        style={inp}
                        value={form.platform || ''}
                        onChange={(e) => setForm({ ...form, platform: e.target.value || '' })}
                      >
                        <option value="">Select source…</option>
                        {PLATFORM_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : (
                    <label>
                      <span style={ui.label(isMobile)}>Approval</span>
                      <input
                        style={{ ...inp, background: '#f8fafc' }}
                        readOnly
                        value={getApprovalStatus(initial || {})}
                      />
                      <div style={ui.help(isMobile)}>Maintenance blocks follow the same approval logic.</div>
                    </label>
                  )}
                </div>

                <label style={{ display: 'block', marginTop: 10 }}>
                  <span style={ui.label(isMobile)}>Notes</span>
                  <textarea
                    rows={4}
                    style={{ ...inp, minHeight: 96, resize: 'vertical', background: '#ffffff' }}
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  />
                </label>

                {tab === 'reservation' && (
                  <>
                    <div style={ui.divider(isMobile)} />

                    {/* Reservation meta */}
                    <div style={ui.grid2(isMobile)}>
                      <label>
                        <span style={ui.label(isMobile)}>Reservation date</span>
                        <div
                          style={ui.prettyPickWrap}
                          onClick={() => {
                            reservationDateRef.current?.showPicker?.();
                            reservationDateRef.current?.focus();
                          }}
                        >
                          <input
                            ref={reservationDateRef}
                            type="datetime-local"
                            lang="en-GB"
                            style={ui.hiddenPickerInput(isMobile)}
                            value={toCompanyDateTimeInputValue(form.reservationDate, companyTz)}
                            onChange={(e) =>
                              setForm((f) => ({
                                ...f,
                                reservationDate: fromCompanyDateTimeInputValue(e.target.value, companyTz),
                              }))
                            }
                          />
                          <div style={ui.prettyOverlay(!!form.reservationDate, isMobile)}>
                            {form.reservationDate ? formatPrettyDateTime(form.reservationDate, companyTz) : 'Select date & time…'}
                          </div>
                        </div>
                      </label>

                      <label>
                        <span style={ui.label(isMobile)}>Reservation ref</span>
                        <input
                          style={inp}
                          value={form.reservationRef}
                          onChange={(e) => setForm({ ...form, reservationRef: e.target.value })}
                        />
                      </label>
                    </div>

                    <div style={{ marginTop: 10, ...ui.grid2(isMobile) }}>
                      <label>
                        <span style={ui.label(isMobile)}>Customer name</span>
                        <input
                          style={inp}
                          value={form.customerName}
                          onChange={(e) => setForm({ ...form, customerName: e.target.value })}
                        />
                      </label>
                      <label>
                        <span style={ui.label(isMobile)}>Flight number</span>
                        <input
                          style={inp}
                          value={form.flightNumber}
                          onChange={(e) => setForm({ ...form, flightNumber: e.target.value })}
                        />
                      </label>
                    </div>

                    <label style={{ display: 'block', marginTop: 10 }}>
                      <span style={ui.label(isMobile)}>Phone numbers</span>
                      <input
                        style={inp}
                        value={form.phoneNumbers}
                        onChange={(e) => setForm({ ...form, phoneNumbers: e.target.value })}
                        placeholder="Comma-separated"
                      />
                      <div style={ui.help(isMobile)}>Example: +995..., +355..., +961...</div>
                    </label>
                  </>
                )}
              </div>
            </div>

            {/* CARD: EXTRAS + FINANCE (reservation only) */}
            {tab === 'reservation' && (
              <div style={ui.card(isMobile)}>
                <div style={ui.cardHead(isMobile)}>
                  <p style={ui.cardTitle(isMobile)}>Extras & Payment</p>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <StatusPill
                      tone={
                        form.paymentStatus === 'PREPAID'
                          ? 'ok'
                          : form.paymentStatus === 'PARTIALLY_PAID'
                            ? 'warn'
                            : 'neutral'
                      }
                    >
                      {form.paymentStatus || 'NOT_PAID'}
                    </StatusPill>
                  </div>
                </div>

                <div style={ui.cardBody(isMobile)}>
                  {/* Extras list */}
                  <div>
                    <p style={ui.sectionTitle(isMobile)}>
                      Extras
                    </p>
                    <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
                      {SEAT_EXTRA_OPTIONS.map((opt) => {
                        const count = seatExtraCountFromExtras(form.extras, opt.value);
                        return (
                          <label key={opt.value} style={ui.checkboxRow(isMobile)}>
                            <div style={ui.checkboxLeft}>
                              <div>
                                <div style={ui.checkboxLabel(isMobile)}>{opt.label}</div>
                                <div style={ui.checkboxHint(isMobile)}>
                                  {count > 0 ? `${count} selected` : `Optional, maximum ${MAX_SEAT_EXTRA_COUNT}`}
                                </div>
                              </div>
                            </div>
                            <input
                              type="number"
                              min={0}
                              max={MAX_SEAT_EXTRA_COUNT}
                              step={1}
                              style={{ ...inp, width: 92, flex: '0 0 92px' }}
                              value={count}
                              onChange={(e) => setSeatCount(opt.value, e.target.value)}
                            />
                          </label>
                        );
                      })}

                      {EXTRA_OPTIONS.filter((opt) => !SEAT_EXTRA_OPTIONS.some((seatOpt) => seatOpt.value === opt.value)).map((opt) => {
                        const checked = Array.isArray(form.extras) && form.extras.includes(opt.value);
                        return (
                          <label key={opt.value} style={ui.checkboxRow(isMobile)}>
                            <div style={ui.checkboxLeft}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleExtra(opt.value)}
                              />
                              <div>
                                <div style={ui.checkboxLabel(isMobile)}>{opt.label}</div>
                                <div style={ui.checkboxHint(isMobile)}>{checked ? 'Selected' : 'Optional'}</div>
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>

                    <div style={{ marginTop: 12 }}>
                      <p style={ui.sectionTitle(isMobile)}>
                        Cross-border countries
                      </p>
                      <div style={ui.help(isMobile)}>
                        Albania only. Select every country the customer intends to enter.
                      </div>
                      <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
                        {CROSS_BORDER_COUNTRY_OPTIONS.map((countryName) => {
                          const checked = crossBorderCountriesFromExtras(form.extras).includes(countryName);
                          return (
                            <label key={countryName} style={ui.checkboxRow(isMobile)}>
                              <div style={ui.checkboxLeft}>
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  disabled={!isAlbaniaCompany}
                                  onChange={() => toggleCrossBorderCountry(countryName)}
                                />
                                <div>
                                  <div style={ui.checkboxLabel(isMobile)}>{countryName}</div>
                                  <div style={ui.checkboxHint(isMobile)}>
                                    {isAlbaniaCompany ? '83 USD per country' : 'Not available for this agency'}
                                  </div>
                                </div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    <div style={{ marginTop: 10, ...ui.grid2(isMobile) }}>
                      <label>
                        <span style={ui.label(isMobile)}>Number of extra drivers</span>
                        <input
                          type="number"
                          min={0}
                          max={MAX_EXTRA_DRIVERS}
                          step={1}
                          style={inp}
                          value={normalizeExtraDriversCount(form.extraDriversCount)}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              extraDriversCount: normalizeExtraDriversCount(e.target.value),
                            }))
                          }
                        />
                        <div style={ui.help(isMobile)}>Set to 0 if there are no extra drivers.</div>
                      </label>
                    </div>
                  </div>

                  <div style={ui.divider(isMobile)} />

                  {/* Insurance */}
                  <div>
                    <p style={ui.sectionTitle(isMobile)}>
                      Insurance
                    </p>

                    <div style={{ marginTop: 10, ...ui.grid2(isMobile) }}>
                      <label>
                        <span style={ui.label(isMobile)}>Coverage status</span>
                        <select
                          style={inp}
                          value={form.insuranceIncluded || ''}
                          onChange={(e) =>
                            setForm({ ...form, insuranceIncluded: e.target.value || '' })
                          }
                        >
                          <option value="">Not specified</option>
                          <option value="INCLUDED">Included</option>
                          <option value="NOT_INCLUDED">Not included</option>
                        </select>
                      </label>

                      <label>
                        <span style={ui.label(isMobile)}>Insurance amount</span>
                        <input
                          type="number"
                          step="0.01"
                          style={inp}
                          value={form.insuranceAmount ?? ''}
                          onChange={(e) =>
                            setForm({ ...form, insuranceAmount: e.target.value })
                          }
                        />
                      </label>
                    </div>

                    <div style={{ marginTop: 10, ...ui.grid2(isMobile) }}>
                      <label>
                        <span style={ui.label(isMobile)}>Insurance currency</span>
                        {currencyOptions.length > 0 ? (
                          <>
                            <select
                              style={inp}
                              value={insuranceCurrencySelectValue}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v === '__OTHER__') {
                                  if (
                                    !form.insuranceCurrency ||
                                    currencyOptions.includes(form.insuranceCurrency)
                                  ) {
                                    setForm({ ...form, insuranceCurrency: '' });
                                  }
                                } else {
                                  setForm({ ...form, insuranceCurrency: v });
                                }
                              }}
                            >
                              <option value="">Select currency...</option>
                              {currencyOptions.map((c) => (
                                <option key={c} value={c}>
                                  {c}
                                </option>
                              ))}
                              <option value="__OTHER__">Other...</option>
                            </select>
                            {(!form.insuranceCurrency ||
                              !currencyOptions.includes(form.insuranceCurrency)) && (
                              <input
                                style={{ ...inp, marginTop: 8 }}
                                placeholder="Custom insurance currency (e.g. USD)"
                                value={form.insuranceCurrency}
                                onChange={(e) =>
                                  setForm({ ...form, insuranceCurrency: e.target.value })
                                }
                              />
                            )}
                          </>
                        ) : (
                          <input
                            style={inp}
                            placeholder="Insurance currency (e.g. USD)"
                            value={form.insuranceCurrency}
                            onChange={(e) =>
                              setForm({ ...form, insuranceCurrency: e.target.value })
                            }
                          />
                        )}
                      </label>
                    </div>

                    <div style={ui.help(isMobile)}>
                      Specify whether insurance is included and the exact charge when needed.
                    </div>
                  </div>

                  <div style={ui.divider(isMobile)} />

                  {/* Money */}
                  <div style={ui.grid2(isMobile)}>
                    <label>
                      <span style={ui.label(isMobile)}>Deposit amount</span>
                      <input
                        type="number"
                        step="0.01"
                        style={inp}
                        value={form.depositAmount}
                        onChange={(e) => setForm({ ...form, depositAmount: e.target.value })}
                      />
                      {recommendedDeposit != null && (
                        <div style={ui.help(isMobile)}>
                          Recommended: {recommendedDeposit}
                          {suggestedDepositCurrency ? ` ${suggestedDepositCurrency}` : ''}
                          {companyLocation === 'GE' && ' (Georgia)'}
                          {companyLocation === 'AL' && ' (Albania)'}
                        </div>
                      )}
                    </label>

                    <label>
                      <span style={ui.label(isMobile)}>Deposit currency</span>
                      {currencyOptions.length > 0 ? (
                        <>
                          <select
                            style={inp}
                            value={depositCurrencySelectValue}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === '__OTHER__') {
                                if (!form.depositCurrency || currencyOptions.includes(form.depositCurrency)) {
                                  setForm({ ...form, depositCurrency: '' });
                                }
                              } else {
                                setForm({ ...form, depositCurrency: v });
                              }
                            }}
                          >
                            <option value="">Select currency…</option>
                            {currencyOptions.map((c) => (
                              <option key={c} value={c}>
                                {c}
                              </option>
                            ))}
                            <option value="__OTHER__">Other…</option>
                          </select>
                          {(!form.depositCurrency || !currencyOptions.includes(form.depositCurrency)) && (
                            <input
                              style={{ ...inp, marginTop: 8 }}
                              placeholder="Custom deposit currency (e.g. LBP)"
                              value={form.depositCurrency}
                              onChange={(e) => setForm({ ...form, depositCurrency: e.target.value })}
                            />
                          )}
                        </>
                      ) : (
                        <input
                          style={inp}
                          placeholder="Deposit currency (e.g. USD)"
                          value={form.depositCurrency}
                          onChange={(e) => setForm({ ...form, depositCurrency: e.target.value })}
                        />
                      )}
                    </label>
                  </div>

                  <div style={{ marginTop: 10, ...ui.grid2(isMobile) }}>
                    <label>
                      <span style={ui.label(isMobile)}>Rental amount</span>
                      <input
                        type="number"
                        step="0.01"
                        style={inp}
                        value={form.rentalAmount}
                        onChange={(e) => {
                          const value = e.target.value;
                          setForm((prev) => {
                            const next = { ...prev, rentalAmount: value };
                            if (prev.paymentStatus === 'PREPAID' && (prev.amountPaid === '' || prev.amountPaid == null)) {
                              next.amountPaid = value;
                            }
                            return next;
                          });
                        }}
                      />
                    </label>

                    <label>
                      <span style={ui.label(isMobile)}>Rental currency</span>
                      {currencyOptions.length > 0 ? (
                        <>
                          <select
                            style={inp}
                            value={rentalCurrencySelectValue}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === '__OTHER__') {
                                if (!form.rentalCurrency || currencyOptions.includes(form.rentalCurrency)) {
                                  setForm({ ...form, rentalCurrency: '' });
                                }
                              } else {
                                setForm({ ...form, rentalCurrency: v });
                              }
                            }}
                          >
                            <option value="">Select currency…</option>
                            {currencyOptions.map((c) => (
                              <option key={c} value={c}>
                                {c}
                              </option>
                            ))}
                            <option value="__OTHER__">Other…</option>
                          </select>
                          {(!form.rentalCurrency || !currencyOptions.includes(form.rentalCurrency)) && (
                            <input
                              style={{ ...inp, marginTop: 8 }}
                              placeholder="Custom rental currency (e.g. LBP)"
                              value={form.rentalCurrency}
                              onChange={(e) => setForm({ ...form, rentalCurrency: e.target.value })}
                            />
                          )}
                        </>
                      ) : (
                        <input
                          style={inp}
                          placeholder="Rental currency (e.g. USD)"
                          value={form.rentalCurrency}
                          onChange={(e) => setForm({ ...form, rentalCurrency: e.target.value })}
                        />
                      )}
                    </label>
                  </div>

                  <div style={ui.divider(isMobile)} />

                  {/* Payment */}
                  <label style={{ display: 'block' }}>
                    <span style={ui.label(isMobile)}>Payment status</span>
                    <select
                      style={inp}
                      value={form.paymentStatus || 'NOT_PAID'}
                      onChange={(e) => {
                        const value = e.target.value;
                        setForm((prev) => {
                          let amountPaid = prev.amountPaid;
                          if (value === 'NOT_PAID') amountPaid = '';
                          else if (value === 'PREPAID') amountPaid = prev.rentalAmount || '';
                          return { ...prev, paymentStatus: value, amountPaid };
                        });
                      }}
                    >
                      <option value="NOT_PAID">Not paid</option>
                      <option value="PARTIALLY_PAID">Partially paid</option>
                      <option value="PREPAID">Prepaid (fully paid)</option>
                    </select>
                    <div style={ui.help(isMobile)}>
                      Use “Partially paid” only when you have a real amount. Otherwise leave as “Not paid”.
                    </div>
                  </label>

                  {form.paymentStatus === 'PARTIALLY_PAID' && (
                    <div style={{ marginTop: 10, ...ui.grid2(isMobile) }}>
                      <label>
                        <span style={ui.label(isMobile)}>Amount paid</span>
                        <input
                          type="number"
                          step="0.01"
                          style={inp}
                          value={form.amountPaid ?? ''}
                          onChange={(e) => setForm((prev) => ({ ...prev, amountPaid: e.target.value }))}
                        />
                      </label>

                      <label>
                        <span style={ui.label(isMobile)}>Remaining (auto)</span>
                        <input
                          style={{ ...inp, background: '#f1f5f9' }}
                          readOnly
                          value={remainingAmountNum == null ? '' : remainingAmountNum.toFixed(2)}
                          placeholder="Will be rental - paid"
                        />
                        {form.rentalCurrency && remainingAmountNum != null && !Number.isNaN(remainingAmountNum) && (
                          <div style={ui.help(isMobile)}>
                            {remainingAmountNum.toFixed(2)} {form.rentalCurrency} remaining
                          </div>
                        )}
                      </label>
                    </div>
                  )}

                  {form.paymentStatus === 'PREPAID' && (
                    <div style={ui.help(isMobile)}>Marked as fully prepaid. Adjust “amount paid” later if needed.</div>
                  )}
                </div>
              </div>
            )}

            {/* CARD: LOCATIONS (reservation only) */}
            {tab === 'reservation' && (
              <div style={ui.card(isMobile)}>
                <div style={ui.cardHead(isMobile)}>
                  <p style={ui.cardTitle(isMobile)}>Locations</p>
                  <span style={ui.badge('neutral')}>Pickup & Dropoff</span>
                </div>

                <div style={ui.cardBody(isMobile)}>
                  <div style={ui.grid2(isMobile)}>
                    <label>
                      <span style={ui.label(isMobile)}>Pickup location</span>
                      {pickupOptions.length > 0 ? (
                        <>
                          <select
                            style={inp}
                            value={pickupSelectValue}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === '__OTHER__') {
                                if (!form.pickupLocation || pickupOptions.includes(form.pickupLocation)) {
                                  setForm({ ...form, pickupLocation: '' });
                                }
                              } else {
                                setForm({ ...form, pickupLocation: v });
                              }
                            }}
                          >
                            <option value="">Select pickup…</option>
                            {pickupOptions.map((loc) => (
                              <option key={loc} value={loc}>
                                {loc}
                              </option>
                            ))}
                            <option value="__OTHER__">Other…</option>
                          </select>
                          {(!form.pickupLocation || !pickupOptions.includes(form.pickupLocation)) && (
                            <input
                              style={{ ...inp, marginTop: 8 }}
                              placeholder="Custom pickup location"
                              value={form.pickupLocation}
                              onChange={(e) => setForm({ ...form, pickupLocation: e.target.value })}
                            />
                          )}
                        </>
                      ) : (
                        <input
                          style={inp}
                          placeholder="Pickup location"
                          value={form.pickupLocation}
                          onChange={(e) => setForm({ ...form, pickupLocation: e.target.value })}
                        />
                      )}
                    </label>

                    <label>
                      <span style={ui.label(isMobile)}>Dropoff location</span>
                      {dropoffOptions.length > 0 ? (
                        <>
                          <select
                            style={inp}
                            value={dropoffSelectValue}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === '__OTHER__') {
                                if (!form.dropoffLocation || dropoffOptions.includes(form.dropoffLocation)) {
                                  setForm({ ...form, dropoffLocation: '' });
                                }
                              } else {
                                setForm({ ...form, dropoffLocation: v });
                              }
                            }}
                          >
                            <option value="">Select dropoff…</option>
                            {dropoffOptions.map((loc) => (
                              <option key={loc} value={loc}>
                                {loc}
                              </option>
                            ))}
                            <option value="__OTHER__">Other…</option>
                          </select>
                          {(!form.dropoffLocation || !dropoffOptions.includes(form.dropoffLocation)) && (
                            <input
                              style={{ ...inp, marginTop: 8 }}
                              placeholder="Custom dropoff location"
                              value={form.dropoffLocation}
                              onChange={(e) => setForm({ ...form, dropoffLocation: e.target.value })}
                            />
                          )}
                        </>
                      ) : (
                        <input
                          style={inp}
                          placeholder="Dropoff location"
                          value={form.dropoffLocation}
                          onChange={(e) => setForm({ ...form, dropoffLocation: e.target.value })}
                        />
                      )}
                    </label>
                  </div>
                </div>
              </div>
            )}
          </fieldset>
        </div>

        {/* FOOTER */}
        <div style={ui.footer(isMobile)}>
          {readOnly ? (
            <div style={{ width: isMobile ? '100%' : 'auto', marginLeft: isMobile ? 0 : 'auto' }}>
              <Button onClick={onClose} style={{ ...ui.footerBtn(isMobile), background: 'white' }}>
                Close
              </Button>
            </div>
          ) : (
            <div style={ui.footerRight(isMobile)}>
              {(mode === 'edit' && form?.id) && (
                <div style={ui.footerSecondaryActions(isMobile)}>
                  {tab === 'reservation' && (
                    <>
                      <Button
                        onClick={() => navigate(`/console/handover/${form.id}/pickup`)}
                        style={{
                          ...ui.footerBtn(isMobile),
                          background: '#ffffff',
                          border: '1px solid #d1d5db',
                        }}
                      >
                        {pickupSigned ? 'View pickup contract' : 'Fill pickup form'}
                      </Button>

                      <Button
                        onClick={() => navigate(`/console/handover/${form.id}/dropoff`)}
                        style={{
                          ...ui.footerBtn(isMobile),
                          background: '#ffffff',
                          border: '1px solid #d1d5db',
                        }}
                      >
                        {dropoffSigned ? 'View dropoff contract' : 'Fill dropoff form'}
                      </Button>

                      <Button
                        onClick={() => navigate(`/console/handover/${form.id}/substitution`)}
                        style={{
                          ...ui.footerBtn(isMobile),
                          background: '#ffffff',
                          border: '1px solid #d1d5db',
                        }}
                      >
                        Substitute car
                      </Button>
                    </>
                  )}

                  <DangerButton
                    onClick={doDelete}
                    disabled={deleting}
                    style={{
                      ...ui.footerBtn(isMobile),
                      ...(isMobile && tab === 'maintenance' ? { gridColumn: '1 / -1' } : {}),
                    }}
                  >
                    {deleting ? 'Deleting…' : tab === 'maintenance' ? 'Delete block' : 'Delete'}
                  </DangerButton>
                </div>
              )}

              <div style={isMobile ? ui.footerPrimaryRow(isMobile) : { display: 'contents' }}>
                <Button onClick={onClose} style={{ ...ui.footerBtn(isMobile), background: 'white' }}>
                  Cancel
                </Button>
                <Button
                  onClick={save}
                  disabled={saving}
                  aria-busy={saving}
                  style={{
                    ...ui.footerBtn(isMobile),
                    cursor: saving ? 'wait' : 'pointer',
                  }}
                >
                  {saving ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
